import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useHistory } from 'react-router-dom';
import DashboardLayout from '../UI/Layout/DashboardLayout';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPlus, faTimes, faEdit, faTrashAlt, faCodeBranch,
    faSpinner, faCircle, faGripVertical, faChevronDown,
    faChevronUp, faCog, faFont, faAlignLeft, faListUl,
    faCheckSquare, faCalendarAlt, faHeading, faCaretDown, faCheck,
    faMobileAlt, faMoon, faSun,
    faEye, faChartBar,
} from '@fortawesome/free-solid-svg-icons';
import { NODE_HOST_URL_v1, WhstappAccountList } from './api/Api';
import Axios from '../../axios/Axios';

/* ─── constants ─── */
const CATEGORIES = [
    'SIGN_UP', 'SIGN_IN', 'APPOINTMENT_BOOKING',
    'LEAD_GENERATION', 'CONTACT_US', 'CUSTOMER_SUPPORT', 'SURVEY', 'OTHER',
];

const CONTENT_MENU = [
    {
        key: 'text', label: 'Text', iconText: 'Aa',
        children: [
            { type: 'large_heading', label: 'Large heading', iconText: 'Aa' },
            { type: 'small_heading', label: 'Small heading', iconText: 'Aa' },
            { type: 'caption', label: 'Caption', iconText: 'Aa' },
            { type: 'body_text', label: 'Body', iconText: 'Aa' },
        ],
    },
    {
        key: 'media', label: 'Media', iconText: '🖼',
        children: [
            { type: 'image', label: 'Image', iconText: '🖼' },
            { type: 'document', label: 'Document', iconText: '📄' },
        ],
    },
    {
        key: 'text_answer', label: 'Text answer', iconText: 'Aa',
        children: [
            { type: 'short_answer', label: 'Short answer', iconText: 'Aa' },
            { type: 'long_answer', label: 'Long answer', iconText: 'Aa' },
            { type: 'date', label: 'Date picker', iconText: '📅' },
        ],
    },
    {
        key: 'selection', label: 'Selection', iconText: '≡',
        children: [
            { type: 'single', label: 'Single choice', iconText: '◎' },
            { type: 'multiple', label: 'Multiple choice', iconText: '☑' },
            { type: 'dropdown', label: 'Drop-down', iconText: '≡' },
            { type: 'opt_in', label: 'Opt in', iconText: '⏺' },
        ],
    },
];

const STATUS_CONFIG = {
    DRAFT: { color: '#d97706', bg: '#fef3c7', border: '#fde68a', label: 'Draft' },
    PUBLISHED: { color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0', label: 'Published' },
    DEPRECATED: { color: '#dc2626', bg: '#fef2f2', border: '#fecaca', label: 'Deprecated' },
    default: { color: '#64748b', bg: '#f8fafc', border: '#e2e8f0', label: 'Unknown' },
};

const resolveFlowStatus = (flow) => {
    if (flow.publish_status === '1' || flow.publish_status === 1) return 'PUBLISHED';
    if (flow.publish_status === '0' || flow.publish_status === 0) return 'DRAFT';
    return flow.status ? String(flow.status).toUpperCase() : 'default';
};

let _uid = 1;
const uid = () => ++_uid;

const makeScreen = () => ({ id: uid(), title: '', body: [], button: 'Continue' });

/* ─── sub-components ─── */

const StatusBadge = ({ status }) => {
    const s = STATUS_CONFIG[status?.toUpperCase()] || STATUS_CONFIG.default;
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: '5px',
            padding: '3px 10px', borderRadius: '20px', fontSize: '11px',
            fontWeight: '600', border: `1px solid ${s.border}`,
            background: s.bg, color: s.color,
        }}>
            <FontAwesomeIcon icon={faCircle} style={{ fontSize: '6px' }} />
            {s.label}
        </span>
    );
};

/* Phone preview */
const PhonePreview = ({ screen, platform = 'android', theme = 'light', interactive = true }) => {
    const dark = theme === 'dark';
    const ios = platform === 'ios';

    const formBg = dark ? '#1c1c1e' : '#fff';
    const textMain = dark ? '#f1f5f9' : '#1a1a2e';
    const textSub = dark ? '#94a3b8' : '#475569';
    const inputBg = dark ? '#2d2d3a' : '#f8fafc';
    const inputBorder = dark ? '#3f3f5a' : '#e2e8f0';
    const chatBg = dark ? '#0a0a0f' : '#ece5dd';
    const headerBg = dark ? '#075e54' : '#075e54';
    const borderColor = dark ? '#2d2d4e' : '#1a1a2e';

    return (
        <div style={{
            width: '220px',
            borderRadius: ios ? '40px' : '20px',
            overflow: 'hidden',
            border: `6px solid ${borderColor}`,
            boxShadow: '0 8px 30px rgba(0,0,0,0.3)',
            background: formBg, flexShrink: 0,
            transition: 'all 0.25s',
        }}>
            {/* status bar */}
            {ios ? (
                <div style={{ background: headerBg, height: '28px', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', paddingBottom: '4px' }}>
                    <div style={{ width: '60px', height: '5px', borderRadius: '3px', background: 'rgba(255,255,255,0.3)' }} />
                </div>
            ) : (
                <div style={{ background: dark ? '#0a0a14' : '#1a1a2e', height: '22px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '0 10px', gap: '5px' }}>
                    <div style={{ width: '12px', height: '6px', borderRadius: '1px', border: '1px solid rgba(255,255,255,0.5)', position: 'relative' }}>
                        <div style={{ position: 'absolute', left: '1px', top: '1px', right: '1px', bottom: '1px', background: 'rgba(255,255,255,0.6)', borderRadius: '1px' }} />
                    </div>
                </div>
            )}
            {/* chat header */}
            <div style={{ background: headerBg, padding: '8px 12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ width: '26px', height: '26px', borderRadius: '50%', background: '#128c7e', flexShrink: 0 }} />
                <span style={{ color: '#fff', fontSize: '12px', fontWeight: '600' }}>Business</span>
            </div>
            {/* form area */}
            <div style={{ padding: '12px 10px', minHeight: '320px', background: chatBg, transition: 'background 0.25s' }}>
                <div style={{ background: formBg, borderRadius: '8px', padding: '10px', boxShadow: dark ? 'none' : '0 1px 3px rgba(0,0,0,0.1)', border: dark ? '1px solid #2d2d4e' : 'none', transition: 'all 0.25s' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                        <span style={{ fontSize: '13px', fontWeight: '700', color: textMain }}>{screen.title}</span>
                        <span style={{ fontSize: '14px', color: textSub, cursor: 'default' }}>×</span>
                    </div>
                    {screen.body.length === 0 ? (
                        <p style={{ fontSize: '10px', color: textSub, lineHeight: '1.5', margin: 0 }}>
                            Select 'Add content' to start building your form. To add new screens, select 'Add new' in the 'Screens' panel.
                        </p>
                    ) : (
                        screen.body.map((item) => (
                            <div key={item.id} style={{ marginBottom: '10px' }}>

                                {/* ── Pure text / heading types ── */}
                                {item.type === 'large_heading' && (
                                    <div style={{ fontSize: '13px', fontWeight: '800', color: textMain, lineHeight: '1.3' }}>
                                        {item.label || <span style={{ opacity: 0.35 }}>Large Heading</span>}
                                    </div>
                                )}
                                {item.type === 'small_heading' && (
                                    <div style={{ fontSize: '11px', fontWeight: '700', color: textMain }}>
                                        {item.label || <span style={{ opacity: 0.35 }}>Small Heading</span>}
                                    </div>
                                )}
                                {item.type === 'caption' && (
                                    <div style={{ fontSize: '9px', color: textSub, fontStyle: 'italic' }}>
                                        {item.label || <span style={{ opacity: 0.35 }}>Caption text</span>}
                                    </div>
                                )}
                                {item.type === 'body_text' && (
                                    <div style={{ fontSize: '10px', color: textSub, lineHeight: '1.5' }}>
                                        {item.label || <span style={{ opacity: 0.35 }}>Body text</span>}
                                    </div>
                                )}

                                {/* ── Image ── */}
                                {item.type === 'image' && (
                                    item.fileUrl ? (
                                        <img src={item.fileUrl} alt="form" style={{ width: '100%', borderRadius: '5px', display: 'block', maxHeight: '80px', objectFit: 'cover' }} />
                                    ) : (
                                        <div style={{ border: `1px dashed ${inputBorder}`, borderRadius: '6px', height: '52px', background: inputBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '3px' }}>
                                            <span style={{ fontSize: '16px' }}>🖼</span>
                                            <span style={{ fontSize: '8px', color: textSub }}>Image</span>
                                        </div>
                                    )
                                )}

                                {/* ── Document ── */}
                                {item.type === 'document' && (
                                    <div>
                                        {item.label ? (
                                            <div style={{ fontSize: '9px', fontWeight: '600', color: textMain, marginBottom: '2px' }}>{item.label}</div>
                                        ) : null}
                                        {item.description ? (
                                            <div style={{ fontSize: '8px', color: textSub, marginBottom: '4px', lineHeight: '1.4' }}>{item.description}</div>
                                        ) : null}
                                        <div style={{ border: `1px dashed ${inputBorder}`, borderRadius: '6px', padding: '7px 8px', background: inputBg, display: 'flex', alignItems: 'center', gap: '5px' }}>
                                            <span style={{ fontSize: '13px', flexShrink: 0 }}>📄</span>
                                            <span style={{ fontSize: '8px', color: textSub }}>Tap to attach document</span>
                                        </div>
                                    </div>
                                )}

                                {/* ── Short answer ── */}
                                {item.type === 'short_answer' && (
                                    <>
                                        <div style={{ fontSize: '9px', fontWeight: '600', color: item.label ? textMain : textSub, marginBottom: '3px', opacity: item.label ? 1 : 0.5 }}>
                                            {item.label || 'Short answer'}{item.required !== false && <span style={{ color: '#ef4444', marginLeft: '2px' }}>*</span>}
                                        </div>
                                        {item.instructions ? <div style={{ fontSize: '8px', color: textSub, marginBottom: '3px', lineHeight: '1.4' }}>{item.instructions}</div> : null}
                                        <div style={{ border: `1px solid ${inputBorder}`, borderRadius: '4px', height: '22px', background: inputBg, display: 'flex', alignItems: 'center', padding: '0 7px', justifyContent: 'space-between' }}>
                                            <span style={{ fontSize: '8px', color: textSub, opacity: 0.55 }}>
                                                {item.inputType === 'email' ? 'name@email.com' :
                                                 item.inputType === 'phone' ? '+1 000 000 0000' :
                                                 item.inputType === 'password' ? '••••••••' :
                                                 item.inputType === 'number' ? '0' :
                                                 item.inputType === 'passcode' ? '• • • •' : ''}
                                            </span>
                                            {item.inputType && item.inputType !== 'text' && (
                                                <span style={{ fontSize: '7px', color: textSub, opacity: 0.45, textTransform: 'uppercase', letterSpacing: '0.4px' }}>{item.inputType}</span>
                                            )}
                                        </div>
                                    </>
                                )}

                                {/* ── Long answer ── */}
                                {item.type === 'long_answer' && (
                                    <>
                                        <div style={{ fontSize: '9px', fontWeight: '600', color: item.label ? textMain : textSub, marginBottom: '3px', opacity: item.label ? 1 : 0.5 }}>
                                            {item.label || 'Long answer'}{item.required !== false && <span style={{ color: '#ef4444', marginLeft: '2px' }}>*</span>}
                                        </div>
                                        {item.instructions ? <div style={{ fontSize: '8px', color: textSub, marginBottom: '3px', lineHeight: '1.4' }}>{item.instructions}</div> : null}
                                        <div style={{ border: `1px solid ${inputBorder}`, borderRadius: '4px', height: '40px', background: inputBg }} />
                                    </>
                                )}

                                {/* ── Date picker ── */}
                                {item.type === 'date' && (
                                    <>
                                        <div style={{ fontSize: '9px', fontWeight: '600', color: item.label ? textMain : textSub, marginBottom: '2px', opacity: item.label ? 1 : 0.5 }}>
                                            {item.label || 'Date'}{item.required !== false && <span style={{ color: '#ef4444', marginLeft: '2px' }}>*</span>}
                                        </div>
                                        {item.instructions ? <div style={{ fontSize: '8px', color: textSub, marginBottom: '3px', lineHeight: '1.4' }}>{item.instructions}</div> : null}
                                        <div style={{ border: `1px solid ${inputBorder}`, borderRadius: '4px', height: '24px', background: inputBg, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 7px' }}>
                                            <span style={{ fontSize: '8px', color: textSub }}>DD / MM / YYYY</span>
                                            <span style={{ fontSize: '10px' }}>📅</span>
                                        </div>
                                    </>
                                )}

                                {/* ── Single / Multiple choice ── */}
                                {(item.type === 'single' || item.type === 'multiple') && (
                                    <>
                                        <div style={{ fontSize: '9px', fontWeight: '600', color: item.label ? textMain : textSub, marginBottom: '4px', opacity: item.label ? 1 : 0.5 }}>
                                            {item.label || (item.type === 'single' ? 'Single choice' : 'Multiple choice')}{item.required !== false && <span style={{ color: '#ef4444', marginLeft: '2px' }}>*</span>}
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                            {(item.options?.length ? item.options : ['Option 1', 'Option 2']).map((o, i) => (
                                                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '3px 6px', borderRadius: '4px', border: `1px solid ${inputBorder}`, background: inputBg }}>
                                                    <div style={{ width: '8px', height: '8px', borderRadius: item.type === 'single' ? '50%' : '2px', border: `1.5px solid ${textSub}`, flexShrink: 0 }} />
                                                    <span style={{ fontSize: '8px', color: textSub }}>{o}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                )}

                                {/* ── Dropdown ── */}
                                {item.type === 'dropdown' && (
                                    <>
                                        <div style={{ fontSize: '9px', fontWeight: '600', color: item.label ? textMain : textSub, marginBottom: '3px', opacity: item.label ? 1 : 0.5 }}>
                                            {item.label || 'Drop-down'}{item.required !== false && <span style={{ color: '#ef4444', marginLeft: '2px' }}>*</span>}
                                        </div>
                                        <div style={{ border: `1px solid ${inputBorder}`, borderRadius: '4px', height: '22px', background: inputBg, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 7px' }}>
                                            <span style={{ fontSize: '8px', color: textSub }}>Select an option</span>
                                            <span style={{ fontSize: '8px', color: textSub }}>▾</span>
                                        </div>
                                    </>
                                )}

                                {/* ── Opt-in ── */}
                                {item.type === 'opt_in' && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '4px 6px', border: `1px solid ${inputBorder}`, borderRadius: '5px', background: inputBg }}>
                                        <div style={{ width: '22px', height: '12px', borderRadius: '6px', background: '#25d366', position: 'relative', flexShrink: 0 }}>
                                            <div style={{ position: 'absolute', right: '2px', top: '2px', width: '8px', height: '8px', borderRadius: '50%', background: '#fff' }} />
                                        </div>
                                        <span style={{ fontSize: '8px', color: textSub, lineHeight: '1.4' }}>
                                            {item.label || <span style={{ opacity: 0.4 }}>Opt in</span>}
                                            {item.required !== false && <span style={{ color: '#ef4444', marginLeft: '2px' }}>*</span>}
                                        </span>
                                    </div>
                                )}

                            </div>
                        ))
                    )}
                    {screen.button && (
                        <div style={{
                            marginTop: '10px', background: '#25d366', borderRadius: '20px',
                            padding: '6px', textAlign: 'center', fontSize: '10px',
                            color: '#fff', fontWeight: '700',
                        }}>
                            {screen.button}
                        </div>
                    )}
                    <div style={{ marginTop: '6px', textAlign: 'center', fontSize: '8px', color: '#94a3b8' }}>
                        Managed by the business. <span style={{ color: '#128c7e' }}>Learn more</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

/* Accordion section */
const Accordion = ({ title, open, onToggle, children }) => (
    <div style={{ borderBottom: '1px solid #e8e8f0' }}>
        <button
            onClick={onToggle}
            style={{
                width: '100%', padding: '12px 16px',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                background: open ? '#f0f7ff' : '#fff', border: 'none', cursor: 'pointer',
                fontSize: '13px', fontWeight: '600', color: '#1a1a2e',
            }}
        >
            {title}
            <FontAwesomeIcon icon={open ? faChevronUp : faChevronDown} style={{ fontSize: '11px', color: '#64748b' }} />
        </button>
        {open && <div style={{ padding: '12px 16px 16px', background: '#fff' }}>{children}</div>}
    </div>
);

/* Content item editor */
const DOCUMENT_MIME_OPTIONS = [
    { value: 'application/pdf', label: 'PDF' },
    { value: 'application/msword', label: 'DOC' },
    { value: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', label: 'DOCX' },
    { value: 'application/vnd.ms-excel', label: 'XLS' },
    { value: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', label: 'XLSX' },
    { value: 'application/vnd.ms-powerpoint', label: 'PPT' },
    { value: 'application/vnd.openxmlformats-officedocument.presentationml.presentation', label: 'PPTX' },
    { value: 'application/vnd.oasis.opendocument.text', label: 'ODT' },
    { value: 'application/vnd.oasis.opendocument.spreadsheet', label: 'ODS' },
    { value: 'application/vnd.oasis.opendocument.presentation', label: 'ODP' },
    { value: 'application/zip', label: 'ZIP' },
    { value: 'application/x-7z-compressed', label: '7Z' },
    { value: 'application/gzip', label: 'GZ' },
];

const ContentItem = ({ item, onChange, onRemove }) => {
    const [dragOver, setDragOver] = useState(false);
    const fileInputRef = useRef(null);

    const optionMax = item.type === 'dropdown' ? 30 : 10;
    const addOption = () => {
        if ((item.options || []).length >= optionMax) return;
        onChange({ ...item, options: [...(item.options || []), `Option ${(item.options?.length || 0) + 1}`] });
    };
    const updateOption = (i, val) => {
        const opts = [...(item.options || [])];
        opts[i] = val;
        onChange({ ...item, options: opts });
    };
    const removeOption = (i) => onChange({ ...item, options: item.options.filter((_, idx) => idx !== i) });

    const handleFile = (file) => {
        if (!file) return;
        const allowed = ['image/jpeg', 'image/png'];
        if (!allowed.includes(file.type)) return onChange({ ...item, fileError: 'Only JPEG and PNG files are allowed.', fileName: null, fileUrl: null });
        if (file.size > 300 * 1024) return onChange({ ...item, fileError: 'File exceeds 300 KB limit.', fileName: null, fileUrl: null });
        const url = URL.createObjectURL(file);
        onChange({ ...item, fileName: file.name, fileUrl: url, fileError: null });
    };

    const typeLabel = CONTENT_MENU.flatMap(c => c.children).find(c => c.type === item.type)?.label || item.type;
    const isImage = item.type === 'image';
    const isDocument = item.type === 'document';
    const isTextOnly = ['large_heading', 'small_heading', 'caption', 'body_text'].includes(item.type);

    return (
        <div style={{ border: '1px solid #e2e8f0', borderRadius: '10px', marginBottom: '10px', background: '#fafafa', overflow: 'hidden' }}>
            {/* Header */}
            <div style={{ padding: '9px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #e2e8f0', background: '#f0f7ff' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FontAwesomeIcon icon={faGripVertical} style={{ color: '#cbd5e1', fontSize: '11px' }} />
                    <span style={{ fontSize: '12px', fontWeight: '700', color: '#1a1a2e' }}>{typeLabel}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {isImage && !item.fileUrl && (
                        <span title="Image required" style={{ color: '#f59e0b', fontSize: '13px' }}>⚠</span>
                    )}
                    <button onClick={onRemove} style={{ border: 'none', background: 'none', color: '#94a3b8', cursor: 'pointer', padding: '0 2px', fontSize: '13px' }}>
                        <FontAwesomeIcon icon={faChevronUp} style={{ fontSize: '10px' }} />
                    </button>
                </div>
            </div>

            {/* Body */}
            <div style={{ padding: '12px 14px' }}>
                {item.type === 'date' ? (
                    <>
                        {/* Label with counter */}
                        <div style={{ position: 'relative', marginBottom: '10px' }}>
                            <input
                                type="text"
                                className="form-control form-control-sm"
                                placeholder="Label"
                                value={item.label}
                                maxLength={20}
                                onChange={e => onChange({ ...item, label: e.target.value })}
                                style={{ fontSize: '12px', paddingRight: '44px' }}
                            />
                            <span style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '10px', color: '#94a3b8', pointerEvents: 'none' }}>
                                {(item.label || '').length}/20
                            </span>
                        </div>

                        {/* Instructions */}
                        <div style={{ marginBottom: '12px' }}>
                            <label style={{ fontSize: '12px', color: '#475569', fontWeight: '500', marginBottom: '4px', display: 'block' }}>
                                Instructions <span style={{ color: '#94a3b8', fontWeight: '400' }}>· Optional</span>
                            </label>
                            <div style={{ position: 'relative' }}>
                                <textarea
                                    className="form-control form-control-sm"
                                    placeholder=""
                                    value={item.instructions || ''}
                                    maxLength={80}
                                    rows={2}
                                    onChange={e => onChange({ ...item, instructions: e.target.value })}
                                    style={{ fontSize: '12px', resize: 'none', paddingRight: '44px', paddingBottom: '20px' }}
                                />
                                <span style={{ position: 'absolute', right: '10px', bottom: '8px', fontSize: '10px', color: '#94a3b8', pointerEvents: 'none' }}>
                                    {(item.instructions || '').length}/80
                                </span>
                            </div>
                        </div>

                        {/* Required toggle + Delete */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '10px' }}>
                            <span style={{ fontSize: '12px', color: '#475569', fontWeight: '500' }}>Required</span>
                            <div
                                onClick={() => onChange({ ...item, required: !item.required })}
                                style={{
                                    width: '36px', height: '20px', borderRadius: '10px', cursor: 'pointer', flexShrink: 0,
                                    background: item.required !== false ? '#2563eb' : '#e2e8f0',
                                    position: 'relative', transition: 'background 0.2s',
                                }}
                            >
                                <div style={{
                                    position: 'absolute', top: '2px',
                                    left: item.required !== false ? '18px' : '2px',
                                    width: '16px', height: '16px', borderRadius: '50%',
                                    background: '#fff', transition: 'left 0.2s',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
                                }} />
                            </div>
                            <button onClick={onRemove} style={{ border: 'none', background: 'none', color: '#94a3b8', cursor: 'pointer', padding: '2px' }}>
                                <FontAwesomeIcon icon={faTrashAlt} style={{ fontSize: '13px' }} />
                            </button>
                        </div>
                    </>
                ) : isImage ? (
                    <>
                        <div style={{ fontSize: '13px', fontWeight: '600', color: '#1a1a2e', marginBottom: '10px' }}>
                            Choose JPG or PNG file
                        </div>

                        {/* Drop zone */}
                        {!item.fileUrl ? (
                            <div
                                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                                onDragLeave={() => setDragOver(false)}
                                onDrop={e => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files[0]); }}
                                style={{
                                    border: `2px dashed ${dragOver ? '#4f46e5' : '#cbd5e1'}`,
                                    borderRadius: '8px', padding: '20px 16px', textAlign: 'center',
                                    background: dragOver ? '#eef2ff' : '#fff',
                                    transition: 'all 0.2s', marginBottom: '10px',
                                }}
                            >
                                <p style={{ fontSize: '12px', color: '#64748b', margin: '0 0 4px' }}>Drag and drop your file</p>
                                <p style={{ fontSize: '12px', color: '#64748b', margin: 0 }}>
                                    Or{' '}
                                    <span
                                        onClick={() => fileInputRef.current?.click()}
                                        style={{ color: '#2563eb', cursor: 'pointer', textDecoration: 'underline' }}
                                    >
                                        choose file on your device
                                    </span>
                                </p>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/jpeg,image/png"
                                    style={{ display: 'none' }}
                                    onChange={e => handleFile(e.target.files[0])}
                                />
                            </div>
                        ) : (
                            <div style={{ position: 'relative', marginBottom: '10px', borderRadius: '8px', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                                <img src={item.fileUrl} alt="preview" style={{ width: '100%', maxHeight: '120px', objectFit: 'cover', display: 'block' }} />
                                <button
                                    onClick={() => onChange({ ...item, fileUrl: null, fileName: null, fileError: null })}
                                    style={{ position: 'absolute', top: '6px', right: '6px', width: '24px', height: '24px', borderRadius: '50%', background: 'rgba(0,0,0,0.5)', border: 'none', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                >
                                    <FontAwesomeIcon icon={faTimes} style={{ fontSize: '10px' }} />
                                </button>
                            </div>
                        )}

                        {/* Error */}
                        {(item.fileError || (!item.fileUrl && item.fileName === null)) && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: '#fef2f2', borderRadius: '6px', marginBottom: '10px' }}>
                                <span style={{ fontSize: '14px' }}>🚫</span>
                                <span style={{ fontSize: '12px', color: '#dc2626' }}>{item.fileError || 'An image must be selected'}</span>
                            </div>
                        )}

                        {/* Info + delete */}
                        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
                            <div>
                                <p style={{ fontSize: '11px', color: '#64748b', margin: '0 0 2px' }}>Maximum file size: 300 KB</p>
                                <p style={{ fontSize: '11px', color: '#64748b', margin: 0 }}>Acceptable file types: JPEG, PNG</p>
                            </div>
                            <button onClick={onRemove} style={{ border: 'none', background: 'none', color: '#94a3b8', cursor: 'pointer', padding: '2px' }}>
                                <FontAwesomeIcon icon={faTrashAlt} style={{ fontSize: '13px' }} />
                            </button>
                        </div>
                    </>
                ) : isDocument ? (
                    <>
                        {/* Label */}
                        <div style={{ marginBottom: '10px' }}>
                            <label style={{ fontSize: '11px', fontWeight: '600', color: '#64748b', marginBottom: '4px', display: 'block' }}>Label</label>
                            <input
                                type="text"
                                className="form-control form-control-sm"
                                placeholder="e.g. Contract"
                                value={item.label || ''}
                                maxLength={60}
                                onChange={e => onChange({ ...item, label: e.target.value })}
                                style={{ fontSize: '12px' }}
                            />
                        </div>

                        {/* Description */}
                        <div style={{ marginBottom: '10px' }}>
                            <label style={{ fontSize: '11px', fontWeight: '600', color: '#64748b', marginBottom: '4px', display: 'block' }}>
                                Description <span style={{ color: '#94a3b8', fontWeight: '400' }}>· Optional</span>
                            </label>
                            <input
                                type="text"
                                className="form-control form-control-sm"
                                placeholder="e.g. Attach the signed copy"
                                value={item.description || ''}
                                maxLength={100}
                                onChange={e => onChange({ ...item, description: e.target.value })}
                                style={{ fontSize: '12px' }}
                            />
                        </div>

                        {/* Min / Max documents */}
                        <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                            <div style={{ flex: 1 }}>
                                <label style={{ fontSize: '11px', fontWeight: '600', color: '#64748b', marginBottom: '4px', display: 'block' }}>Min files</label>
                                <input
                                    type="number"
                                    className="form-control form-control-sm"
                                    min={0} max={10}
                                    value={item.minDocs ?? 1}
                                    onChange={e => onChange({ ...item, minDocs: Math.max(0, Math.min(10, +e.target.value || 0)) })}
                                    style={{ fontSize: '12px' }}
                                />
                            </div>
                            <div style={{ flex: 1 }}>
                                <label style={{ fontSize: '11px', fontWeight: '600', color: '#64748b', marginBottom: '4px', display: 'block' }}>Max files</label>
                                <input
                                    type="number"
                                    className="form-control form-control-sm"
                                    min={1} max={10}
                                    value={item.maxDocs ?? 1}
                                    onChange={e => onChange({ ...item, maxDocs: Math.max(1, Math.min(10, +e.target.value || 1)) })}
                                    style={{ fontSize: '12px' }}
                                />
                            </div>
                            <div style={{ flex: 1 }}>
                                <label style={{ fontSize: '11px', fontWeight: '600', color: '#64748b', marginBottom: '4px', display: 'block' }}>Max size (KB)</label>
                                <input
                                    type="number"
                                    className="form-control form-control-sm"
                                    min={1}
                                    value={item.maxFileSizeKb ?? 1024}
                                    onChange={e => onChange({ ...item, maxFileSizeKb: Math.max(1, +e.target.value || 1024) })}
                                    style={{ fontSize: '12px' }}
                                />
                            </div>
                        </div>

                        {/* Allowed MIME types */}
                        <div style={{ marginBottom: '10px' }}>
                            <label style={{ fontSize: '11px', fontWeight: '600', color: '#64748b', marginBottom: '6px', display: 'block' }}>Allowed file types</label>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                {DOCUMENT_MIME_OPTIONS.map(opt => {
                                    const selected = (item.allowedMimeTypes || []).includes(opt.value);
                                    return (
                                        <button
                                            key={opt.value}
                                            onClick={() => {
                                                const current = item.allowedMimeTypes || [];
                                                const next = selected
                                                    ? current.filter(v => v !== opt.value)
                                                    : [...current, opt.value];
                                                onChange({ ...item, allowedMimeTypes: next });
                                            }}
                                            style={{
                                                padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '600',
                                                border: `1px solid ${selected ? '#4f46e5' : '#e2e8f0'}`,
                                                background: selected ? '#eef2ff' : '#f8fafc',
                                                color: selected ? '#4f46e5' : '#64748b',
                                                cursor: 'pointer',
                                            }}
                                        >
                                            {opt.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Delete */}
                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <button onClick={onRemove} style={{ border: 'none', background: 'none', color: '#94a3b8', cursor: 'pointer', padding: '2px' }}>
                                <FontAwesomeIcon icon={faTrashAlt} style={{ fontSize: '13px' }} />
                            </button>
                        </div>
                    </>
                ) : item.type === 'short_answer' ? (
                    <>
                        {/* Input type dropdown */}
                        <div style={{ marginBottom: '10px' }}>
                            <select
                                className="form-control form-control-sm"
                                value={item.inputType || 'text'}
                                onChange={e => onChange({ ...item, inputType: e.target.value })}
                                style={{ fontSize: '12px' }}
                            >
                                <option value="text">Text</option>
                                <option value="password">Password</option>
                                <option value="email">Email</option>
                                <option value="number">Number</option>
                                <option value="passcode">Passcode</option>
                                <option value="phone">Phone</option>
                            </select>
                        </div>
                        {/* Label with character counter */}
                        <div style={{ position: 'relative', marginBottom: '10px' }}>
                            <input
                                type="text"
                                className="form-control form-control-sm"
                                placeholder="Label"
                                value={item.label}
                                maxLength={20}
                                onChange={e => onChange({ ...item, label: e.target.value })}
                                style={{ fontSize: '12px', paddingRight: '44px' }}
                            />
                            <span style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '10px', color: '#94a3b8', pointerEvents: 'none' }}>
                                {(item.label || '').length}/20
                            </span>
                        </div>
                        {/* Instructions */}
                        <div style={{ marginBottom: '4px' }}>
                            <label style={{ fontSize: '12px', color: '#475569', fontWeight: '500', marginBottom: '4px', display: 'block' }}>
                                Instructions <span style={{ color: '#94a3b8', fontWeight: '400' }}>· Optional</span>
                            </label>
                            <div style={{ position: 'relative' }}>
                                <textarea
                                    className="form-control form-control-sm"
                                    placeholder=""
                                    value={item.instructions || ''}
                                    maxLength={80}
                                    rows={2}
                                    onChange={e => onChange({ ...item, instructions: e.target.value })}
                                    style={{ fontSize: '12px', resize: 'none', paddingRight: '44px', paddingBottom: '20px' }}
                                />
                                <span style={{ position: 'absolute', right: '10px', bottom: '8px', fontSize: '10px', color: '#94a3b8', pointerEvents: 'none' }}>
                                    {(item.instructions || '').length}/80
                                </span>
                            </div>
                        </div>
                        {/* Required toggle + Delete */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '10px', marginTop: '10px' }}>
                            <span style={{ fontSize: '12px', color: '#475569', fontWeight: '500' }}>Required</span>
                            <div
                                onClick={() => onChange({ ...item, required: item.required === false ? true : false })}
                                style={{
                                    width: '36px', height: '20px', borderRadius: '10px', cursor: 'pointer', flexShrink: 0,
                                    background: item.required !== false ? '#2563eb' : '#e2e8f0',
                                    position: 'relative', transition: 'background 0.2s',
                                }}
                            >
                                <div style={{
                                    position: 'absolute', top: '2px',
                                    left: item.required !== false ? '18px' : '2px',
                                    width: '16px', height: '16px', borderRadius: '50%',
                                    background: '#fff', transition: 'left 0.2s',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
                                }} />
                            </div>
                            <button onClick={onRemove} style={{ border: 'none', background: 'none', color: '#94a3b8', cursor: 'pointer', padding: '2px' }}>
                                <FontAwesomeIcon icon={faTrashAlt} style={{ fontSize: '13px' }} />
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="Label"
                            value={item.label}
                            onChange={e => onChange({ ...item, label: e.target.value })}
                            style={{ fontSize: '12px', marginBottom: (item.type === 'single' || item.type === 'multiple' || item.type === 'dropdown') ? '8px' : '0' }}
                        />
                        {item.type === 'long_answer' && (
                            <div style={{ marginTop: '10px', marginBottom: '4px' }}>
                                <label style={{ fontSize: '12px', color: '#475569', fontWeight: '500', marginBottom: '4px', display: 'block' }}>
                                    Instructions <span style={{ color: '#94a3b8', fontWeight: '400' }}>· Optional</span>
                                </label>
                                <div style={{ position: 'relative' }}>
                                    <textarea
                                        className="form-control form-control-sm"
                                        placeholder=""
                                        value={item.instructions || ''}
                                        maxLength={80}
                                        rows={2}
                                        onChange={e => onChange({ ...item, instructions: e.target.value })}
                                        style={{ fontSize: '12px', resize: 'none', paddingRight: '44px', paddingBottom: '20px' }}
                                    />
                                    <span style={{ position: 'absolute', right: '10px', bottom: '8px', fontSize: '10px', color: '#94a3b8', pointerEvents: 'none' }}>
                                        {(item.instructions || '').length}/80
                                    </span>
                                </div>
                            </div>
                        )}
                        {(item.type === 'single' || item.type === 'multiple') && (
                            <div style={{ marginTop: '6px' }}>
                                {(item.options || []).map((opt, i) => (
                                    <div key={i} style={{ display: 'flex', gap: '6px', marginBottom: '5px' }}>
                                        <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            value={opt}
                                            onChange={e => updateOption(i, e.target.value)}
                                            style={{ fontSize: '12px', flex: 1 }}
                                        />
                                        <button onClick={() => removeOption(i)} style={{ border: 'none', background: 'none', color: '#dc2626', cursor: 'pointer', padding: '0 4px' }}>
                                            <FontAwesomeIcon icon={faTimes} style={{ fontSize: '10px' }} />
                                        </button>
                                    </div>
                                ))}
                                {(item.options || []).length < optionMax ? (
                                    <button onClick={addOption} style={{ fontSize: '11px', color: '#4f46e5', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontWeight: '600' }}>
                                        + Add option
                                    </button>
                                ) : (
                                    <span style={{ fontSize: '11px', color: '#94a3b8' }}>Max {optionMax} options reached</span>
                                )}
                            </div>
                        )}
                        {item.type === 'dropdown' && (
                            <div style={{ marginTop: '6px' }}>
                                {(item.options || []).map((opt, i) => (
                                    <div key={i} style={{ display: 'flex', gap: '6px', marginBottom: '5px' }}>
                                        <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            value={opt}
                                            onChange={e => updateOption(i, e.target.value)}
                                            style={{ fontSize: '12px', flex: 1 }}
                                        />
                                        <button onClick={() => removeOption(i)} style={{ border: 'none', background: 'none', color: '#dc2626', cursor: 'pointer', padding: '0 4px' }}>
                                            <FontAwesomeIcon icon={faTimes} style={{ fontSize: '10px' }} />
                                        </button>
                                    </div>
                                ))}
                                {(item.options || []).length < optionMax ? (
                                    <button onClick={addOption} style={{ fontSize: '11px', color: '#4f46e5', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontWeight: '600' }}>
                                        + Add option
                                    </button>
                                ) : (
                                    <span style={{ fontSize: '11px', color: '#94a3b8' }}>Max {optionMax} options reached</span>
                                )}
                            </div>
                        )}
                        {/* Required toggle + delete */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '10px', marginTop: '10px' }}>
                            {!isTextOnly && (
                                <>
                                    <span style={{ fontSize: '12px', color: '#475569', fontWeight: '500' }}>Required</span>
                                    <div
                                        onClick={() => onChange({ ...item, required: item.required === false ? true : false })}
                                        style={{
                                            width: '36px', height: '20px', borderRadius: '10px', cursor: 'pointer', flexShrink: 0,
                                            background: item.required !== false ? '#2563eb' : '#e2e8f0',
                                            position: 'relative', transition: 'background 0.2s',
                                        }}
                                    >
                                        <div style={{
                                            position: 'absolute', top: '2px',
                                            left: item.required !== false ? '18px' : '2px',
                                            width: '16px', height: '16px', borderRadius: '50%',
                                            background: '#fff', transition: 'left 0.2s',
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
                                        }} />
                                    </div>
                                </>
                            )}
                            <button onClick={onRemove} style={{ border: 'none', background: 'none', color: '#94a3b8', cursor: 'pointer', padding: '2px' }}>
                                <FontAwesomeIcon icon={faTrashAlt} style={{ fontSize: '13px' }} />
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

/* ─── Flow JSON helpers ─── */

const generateFlowJson = (screens) => {
    const waScreens = (screens || []).map((screen, idx) => {
        const isLast = idx === screens.length - 1;
        const children = [];
        const payload = {};

        if (!screen.body || screen.body.length === 0) {
            children.push({ type: 'TextBody', text: "Select 'Add content' to start building your form." });
        } else {
            screen.body.forEach(item => {
                const n = `field_${item.id}`;
                switch (item.type) {
                    case 'large_heading':
                        children.push({ type: 'TextHeading', text: item.label || '' });
                        break;
                    case 'small_heading':
                        children.push({ type: 'TextSubheading', text: item.label || '' });
                        break;
                    case 'caption':
                        children.push({ type: 'TextCaption', text: item.label || '' });
                        break;
                    case 'body_text':
                        children.push({ type: 'TextBody', text: item.label || '' });
                        break;
                    case 'short_answer':
                        children.push({ type: 'TextInput', label: item.label || '', name: (item.label || '').replace(/ /g, '_').replace(/[^a-zA-Z0-9_]/g, '').toLowerCase(), required: item.required !== false, 'input-type': item.inputType || 'text', ...(item.instructions ? { 'helper-text': item.instructions } : {}) });
                        payload[(item.label || '').replace(/ /g, '_').replace(/[^a-zA-Z0-9_]/g, '').toLowerCase()] = `\${form.${(item.label || '').replace(/ /g, '_').replace(/[^a-zA-Z0-9_]/g, '').toLowerCase()}}`;
                        break;
                    case 'long_answer':
                        children.push({ type: 'TextArea', label: item.label || '', name: (item.label || '').replace(/ /g, '_').replace(/[^a-zA-Z0-9_]/g, '').toLowerCase(), required: item.required !== false, ...(item.instructions ? { 'helper-text': item.instructions } : {}) });
                        payload[(item.label || '').replace(/ /g, '_').replace(/[^a-zA-Z0-9_]/g, '').toLowerCase()] = `\${form.${(item.label || '').replace(/ /g, '_').replace(/[^a-zA-Z0-9_]/g, '').toLowerCase()}}`;
                        break;
                    case 'date':
                        children.push({ type: 'DatePicker', label: item.label || '', name: (item.label || '').replace(/ /g, '_').replace(/[^a-zA-Z0-9_]/g, '').toLowerCase(), required: item.required !== false });
                        payload[(item.label || '').replace(/ /g, '_').replace(/[^a-zA-Z0-9_]/g, '').toLowerCase()] = `\${form.${(item.label || '').replace(/ /g, '_').replace(/[^a-zA-Z0-9_]/g, '').toLowerCase()}}`;
                        break;
                    case 'single':
                        children.push({ type: 'RadioButtonsGroup', label: item.label || '', name: (item.label || '').replace(/ /g, '_').replace(/[^a-zA-Z0-9_]/g, '').toLowerCase(), required: item.required !== false, 'data-source': (item.options || []).map((o, i) => ({ id: `opt_${i}`, title: o })) });
                        payload[(item.label || '').replace(/ /g, '_').replace(/[^a-zA-Z0-9_]/g, '').toLowerCase()] = `\${form.${(item.label || '').replace(/ /g, '_').replace(/[^a-zA-Z0-9_]/g, '').toLowerCase()}}`;
                        break;
                    case 'multiple':
                        children.push({ type: 'CheckboxGroup', label: item.label || '', name: (item.label || '').replace(/ /g, '_').replace(/[^a-zA-Z0-9_]/g, '').toLowerCase(), required: item.required !== false, 'data-source': (item.options || []).map((o, i) => ({ id: `opt_${i}`, title: o })) });
                        payload[(item.label || '').replace(/ /g, '_').replace(/[^a-zA-Z0-9_]/g, '').toLowerCase()] = `\${form.${(item.label || '').replace(/ /g, '_').replace(/[^a-zA-Z0-9_]/g, '').toLowerCase()}}`;
                        break;
                    case 'dropdown':
                        children.push({ type: 'Dropdown', label: item.label || '', name: (item.label || '').replace(/ /g, '_').replace(/[^a-zA-Z0-9_]/g, '').toLowerCase(), required: item.required !== false, 'data-source': (item.options || []).map((o, i) => ({ id: `opt_${i}`, title: o })) });
                        payload[(item.label || '').replace(/ /g, '_').replace(/[^a-zA-Z0-9_]/g, '').toLowerCase()] = `\${form.${(item.label || '').replace(/ /g, '_').replace(/[^a-zA-Z0-9_]/g, '').toLowerCase()}}`;
                        break;
                    case 'opt_in':
                        children.push({ type: 'OptIn', label: item.label || '', name: (item.label || '').replace(/ /g, '_').replace(/[^a-zA-Z0-9_]/g, '').toLowerCase(), required: item.required !== false });
                        payload[(item.label || '').replace(/ /g, '_').replace(/[^a-zA-Z0-9_]/g, '').toLowerCase()] = `\${form.${(item.label || '').replace(/ /g, '_').replace(/[^a-zA-Z0-9_]/g, '').toLowerCase()}}`;
                        break;
                    case 'document': {
                        const docName = (item.label || 'document').replace(/ /g, '_').replace(/[^a-zA-Z0-9_]/g, '').toLowerCase() || 'document_picker';
                        const docNode = {
                            type: 'DocumentPicker',
                            name: docName,
                            label: item.label || '',
                            'min-uploaded-documents': item.minDocs ?? 1,
                            'max-uploaded-documents': item.maxDocs ?? 1,
                            'max-file-size-kb': item.maxFileSizeKb ?? 1024,
                            'allowed-mime-types': item.allowedMimeTypes && item.allowedMimeTypes.length > 0 ? item.allowedMimeTypes : ['application/pdf'],
                        };
                        if (item.description) docNode.description = item.description;
                        children.push(docNode);
                        payload[docName] = `\${form.${docName}}`;
                        break;
                    }
                    default: break;
                }
            });
        }

        children.push({
            type: 'Footer',
            label: screen.button || 'Continue',
            'on-click-action': isLast
                ? { name: 'complete', payload }
                : { name: 'navigate', next: { type: 'screen', name: idx === 0 ? 'SCREEN_2' : `SCREEN_${idx + 2}` }, payload },
        });

        return {
            id: idx === 0 ? 'START' : `SCREEN_${idx + 1}`,
            title: screen.title,
            data: {},
            terminal: isLast,
            ...(isLast ? { success: true } : {}),
            layout: { type: 'SingleColumnLayout', children },
        };
    });

    return JSON.stringify({ version: '7.3', screens: waScreens }, null, 2);
};

const waScreenToPreviewScreen = (waScreen) => {
    if (!waScreen) return { id: 1, title: '', body: [], button: 'Continue' };
    const allChildren = waScreen.layout?.children || [];
    const footer = allChildren.find(c => c.type === 'Footer');
    const bodyItems = allChildren.filter(c => c.type !== 'Footer');

    const body = bodyItems.map((item, idx) => {
        switch (item.type) {
            case 'TextHeading': return { id: idx, type: 'large_heading', label: item.text || '' };
            case 'TextSubheading': return { id: idx, type: 'small_heading', label: item.text || '' };
            case 'TextCaption': return { id: idx, type: 'caption', label: item.text || '' };
            case 'TextBody': return { id: idx, type: 'body_text', label: item.text || '' };
            case 'TextInput': return { id: idx, type: 'short_answer', label: item.label || '', inputType: item['input-type'] || 'text', ...(item['helper-text'] ? { instructions: item['helper-text'] } : {}) };
            case 'TextArea': return { id: idx, type: 'long_answer', label: item.label || '', ...(item['helper-text'] ? { instructions: item['helper-text'] } : {}) };
            case 'DatePicker': return { id: idx, type: 'date', label: item.label || '', required: item.required !== false };
            case 'RadioButtonsGroup': return { id: idx, type: 'single', label: item.label || '', options: (item['data-source'] || []).map(o => o.title) };
            case 'CheckboxGroup': return { id: idx, type: 'multiple', label: item.label || '', options: (item['data-source'] || []).map(o => o.title) };
            case 'Dropdown': return { id: idx, type: 'dropdown', label: item.label || '', options: (item['data-source'] || []).map(o => o.title) };
            case 'OptIn': return { id: idx, type: 'opt_in', label: item.label || '' };
            case 'DocumentPicker': return { id: idx, type: 'document', label: item.label || '', description: item.description || '', minDocs: item['min-uploaded-documents'] ?? 1, maxDocs: item['max-uploaded-documents'] ?? 1, maxFileSizeKb: item['max-file-size-kb'] ?? 1024, allowedMimeTypes: item['allowed-mime-types'] || [] };
            default: return null;
        }
    }).filter(Boolean);

    return { id: waScreen.id, title: waScreen.title || '', body, button: footer?.label || 'Continue' };
};

const useWindowWidth = () => {
    const [w, setW] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
    useEffect(() => {
        const handler = () => setW(window.innerWidth);
        window.addEventListener('resize', handler);
        return () => window.removeEventListener('resize', handler);
    }, []);
    return w;
};

/* ─── main component ─── */
const WhatsappFlowBuilder = () => {
    const history = useHistory();
    const lStorage = JSON.parse(localStorage.getItem('login'));
    const userId = lStorage?.user?.userId;
    const userToken = lStorage?.token;

    const DEMO_FLOWS = [
        { id: 'flow_001', name: 'Customer Support Form', category: 'CUSTOMER_SUPPORT', status: 'PUBLISHED', created_at: '2026-05-10' },
        { id: 'flow_002', name: 'Lead Generation Survey', category: 'LEAD_GENERATION', status: 'DRAFT', created_at: '2026-05-18' },
        { id: 'flow_003', name: 'Appointment Booking Flow', category: 'APPOINTMENT_BOOKING', status: 'PUBLISHED', created_at: '2026-05-25' },
        { id: 'flow_004', name: 'User Sign Up', category: 'SIGN_UP', status: 'DEPRECATED', created_at: '2026-04-30' },
        { id: 'flow_005', name: 'Product Feedback', category: 'SURVEY', status: 'DRAFT', created_at: '2026-06-01' },
    ];

    const [brandNumberList, setBrandNumberList] = useState([]);
    const [selectedBrandNumber, setSelectedBrandNumber] = useState('');
    const [migrateBrandNumber,setMigrateBrandNumber] = useState('')

    const [flows, setFlows] = useState(DEMO_FLOWS);
    const [loading, setLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [migrateLoading, setMigrateLoading] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [alert, setAlert] = useState({ show: false, type: '', msg: '' });

    /* flow builder modal */
    const [builderOpen, setBuilderOpen] = useState(false);
    const [screens, setScreens] = useState([makeScreen()]);
    const [activeId, setActiveId] = useState(null);
    const [openSections, setOpenSections] = useState({ title: true, body: true, button: false });
    const [showAddContent, setShowAddContent] = useState(false);
    const [hoveredCategory, setHoveredCategory] = useState(null);
    const [flowName, setFlowName] = useState('');
    const [flowCategory, setFlowCategory] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});
    const [showSettings, setShowSettings] = useState(false);
    const [previewSettings, setPreviewSettings] = useState({ interactive: true, platform: 'android', theme: 'light' });
    const settingsRef = useRef(null);
    const [showAddScreen, setShowAddScreen] = useState(false);
    const [newScreenName, setNewScreenName] = useState('');
    const addContentRef = useRef(null);
    const [dragBodyIndex, setDragBodyIndex] = useState(null);
    const [dragBodyOver, setDragBodyOver] = useState(null);
    const [builderMobileTab, setBuilderMobileTab] = useState('edit');
    const [editMobileTab, setEditMobileTab] = useState('editor');

    const windowWidth = useWindowWidth();
    const isMobile = windowWidth < 768;
    const isTablet = windowWidth < 1100;

    /* edit view */
    const [editTarget, setEditTarget] = useState(null);
    const [editJson, setEditJson] = useState('');
    const [editJsonError, setEditJsonError] = useState(null);
    const [editPreviewScreenIdx, setEditPreviewScreenIdx] = useState(0);
    const [editPreviewParsed, setEditPreviewParsed] = useState(null);
    const [editLoading, setEditLoading] = useState(false);

    /* button tracker */
    const [trackerTarget, setTrackerTarget] = useState(null);
    const [trackerLoading, setTrackerLoading] = useState(false);
    const [trackerData, setTrackerData] = useState([]);

    const activeScreen = screens.find(s => s.id === activeId) || screens[0];

    const showAlert = (msg, type = 'success') => {
        setAlert({ show: true, type, msg });
        setTimeout(() => setAlert({ show: false, type: '', msg: '' }), 3500);
    };

    const fetchFlows = async () => {
        setLoading(true);
        try {
            const { data } = await Axios.post(
                'https://napi3.authkey.io/api2/v1/user/flow',
                { user_id: userId, token: userToken, method: 'retrieve_flow' }
            );
            if (data.success) {
                setFlows(Array.isArray(data.message) ? data.message : []);
            } else {
                showAlert(data.message || 'Failed to load flows.', 'danger');
            }
        } catch (err) {
            showAlert(err.response?.data?.message || err.message, 'danger');
        } finally {
            setLoading(false);
        }
    };

    const getBrandNumber = () => {
        const paramData = {
            user_id: userId,
            token: userToken,
            method: 'retrieve',
        };
        WhstappAccountList(paramData).then((res) => {
            if (res.data.success === true) {
                setBrandNumberList(res.data.data);
                setMigrateBrandNumber(res.data.data[0].brand_number)
            }
        });
    };

    useEffect(() => {
        fetchFlows();
        getBrandNumber();
    }, []);

    const openEditFlow = async (flow) => {
        console.log(flow);
        setEditTarget(flow);
        setEditJson('');
        setEditPreviewParsed(null);
        setEditPreviewScreenIdx(0);
        setEditJsonError(null);

        let flowJson = flow.flow_json;

        const isEmptyFlowJson = (val) => {
            if (!val) return true;
            try { const p = JSON.parse(val); return p && typeof p === 'object' && !Array.isArray(p) && Object.keys(p).length === 0; } catch (_) { return false; }
        };

        if (isEmptyFlowJson(flowJson)) {
            setEditLoading(true);
            try {
                const { data } = await Axios.post(
                    'https://napi3.authkey.io/api2/v1/user/flow',
                    {
                        user_id: userId,
                        token: userToken,
                        brand_number: flow.brand_number || (brandNumberList[0]?.brand_number ?? ''),
                        method: 'update_flow_json',
                        flow_id: flow.meta_flow_id,
                    }
                );
                if (data.success) {
                    flowJson = typeof data.message === 'string'
                        ? data.message
                        : (data.message?.flow_json ?? data.flow_json ?? null);
                }
            } catch (_) { }
            setEditLoading(false);
        }

        let parsedFlowJson = null;
        try { parsedFlowJson = flowJson ? JSON.parse(flowJson) : null; } catch (_) { }
        const demoScreens = [{ id: 1, title: flow.flow_title, body: [], button: 'Continue' }];
        const json = parsedFlowJson ? JSON.stringify(parsedFlowJson, null, 2) : generateFlowJson(demoScreens);
        let parsed = null;
        try { parsed = JSON.parse(json); } catch (_) { }
        setEditJson(json);
        setEditPreviewParsed(parsed);
    };

    const openButtonTracker = async (flow) => {
        setTrackerTarget(flow);
        setTrackerLoading(true);
        setTrackerData([]);
        try {
            const { data } = await Axios.post(
                'https://napi3.authkey.io/api2/v1/user/flow',
                { user_id: userId, token: userToken, method: 'get_button_tracker', flow_id: flow.meta_flow_id }
            );
            if (data.success && Array.isArray(data.message)) {
                setTrackerData(data.message);
            }
        } catch (_) {}
        setTrackerLoading(false);
    };

    const handleRunJson = () => {
        try {
            const parsed = JSON.parse(editJson);
            setEditPreviewParsed(parsed);
            setEditJsonError(null);
            setEditPreviewScreenIdx(0);
        } catch (e) {
            setEditJsonError(e.message);
            setEditPreviewParsed(null);
        }
    };

    const getEditPreviewScreens = () => editPreviewParsed?.screens || [];

    /* click outside to close Add content menu */
    useEffect(() => {
        const handler = (e) => {
            if (addContentRef.current && !addContentRef.current.contains(e.target)) {
                setShowAddContent(false);
            }
            if (settingsRef.current && !settingsRef.current.contains(e.target)) {
                setShowSettings(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const openBuilder = () => {
        const first = makeScreen();
        setScreens([first]);
        setActiveId(first.id);
        setOpenSections({ title: true, body: true, button: false });
        setFlowName('');
        setFlowCategory('');
        setFieldErrors({});
        setShowAddContent(false);
        setHoveredCategory(null);
        setShowAddScreen(false);
        setNewScreenName('');
        setBuilderMobileTab('edit');
        setBuilderOpen(true);
    };

    const updateScreen = (id, patch) =>
        setScreens(prev => prev.map(s => s.id === id ? { ...s, ...patch } : s));

    const addScreen = () => {
        const name = newScreenName.trim();
        if (!name) return;
        const s = { ...makeScreen(), title: name };
        setScreens(prev => [...prev, s]);
        setActiveId(s.id);
        setNewScreenName('');
        setShowAddScreen(false);
    };

    const removeScreen = (id) => {
        if (screens.length === 1) return;
        const remaining = screens.filter(s => s.id !== id);
        setScreens(remaining);
        if (activeId === id) setActiveId(remaining[0].id);
    };

    const HEADING_TYPES = ['large_heading', 'small_heading', 'caption', 'body_text', 'image', 'document'];

    const addContentItem = (type) => {
        if (activeScreen.body.length >= 8) return;
        const item = {
            id: uid(), type, label: '',
            options: (type === 'single' || type === 'multiple' || type === 'dropdown') ? ['Option 1'] : [],
            ...(!HEADING_TYPES.includes(type) ? { required: true } : {}),
            ...((type === 'date' || type === 'short_answer' || type === 'long_answer') ? { instructions: '' } : {}),
            ...(type === 'short_answer' ? { inputType: 'text' } : {}),
            ...(type === 'document' ? { description: '', minDocs: 1, maxDocs: 1, maxFileSizeKb: 1024, allowedMimeTypes: ['application/pdf'] } : {}),
        };
        updateScreen(activeScreen.id, { body: [...activeScreen.body, item] });
        setShowAddContent(false);
        setHoveredCategory(null);
    };

    const updateContentItem = (itemId, patch) => {
        const newBody = activeScreen.body.map(b => b.id === itemId ? { ...b, ...patch } : b);
        updateScreen(activeScreen.id, { body: newBody });
    };

    const removeContentItem = (itemId) => {
        updateScreen(activeScreen.id, { body: activeScreen.body.filter(b => b.id !== itemId) });
    };

    const reorderBody = (fromIndex, toIndex) => {
        const newBody = [...activeScreen.body];
        const [moved] = newBody.splice(fromIndex, 1);
        newBody.splice(toIndex, 0, moved);
        updateScreen(activeScreen.id, { body: newBody });
    };

    const handleSave = async () => {
        const errors = {};
        if (!flowName.trim()) errors.flowName = 'Flow name is required.';
        if (!flowCategory) errors.flowCategory = 'Please select a category.';
        if (!activeScreen?.title?.trim()) errors.screenTitle = 'Screen title is required.';
        if (!selectedBrandNumber) errors.brandNumber = 'Please select a brand number.';
        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            return;
        }
        setFieldErrors({});
        setActionLoading(true);
        try {

            const flowJson = JSON.parse(generateFlowJson(screens));
            const { data } = await Axios.post(
                `https://napi3.authkey.io/api2/v1/user/flow`,
                {
                    user_id: userId,
                    method: 'create_flow',
                    token: userToken,
                    flow_name: flowName.trim(),
                    flow_category: flowCategory,
                    // categories: [flowCategory],
                    flow_json: flowJson,
                    brand_number: selectedBrandNumber
                    // publish: true,
                },
                // { headers: { Authorization: `Bearer ${userToken}` } }
            );
            if (data.success) {
                showAlert('Flow created successfully.');
                setBuilderOpen(false);
                fetchFlows();
            } else {
                showAlert(data.message || 'Failed to create flow.', 'danger');
            }
        } catch (err) {
            showAlert(err.response?.data?.message || err.message, 'danger');
        } finally {
            setActionLoading(false);
        }
    };

    const handleMigrateFlow = async () => {
        setMigrateLoading(true);
        try {
            const { data } = await Axios.post(
                'https://napi3.authkey.io/api2/v1/user/flow',
                {
                    user_id: userId,
                    token: userToken,
                    method: 'migrate_flow',
                    brand_number: migrateBrandNumber,
                }
            );
            if (data.success) {
                showAlert('Flow migrated successfully.');
                fetchFlows();
            } else {
                showAlert(data.message || 'Failed to migrate flow.', 'danger');
            }
        } catch (err) {
            showAlert(err.response?.data?.message || err.message, 'danger');
        } finally {
            setMigrateLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        setActionLoading(true);
        try {
            const { data } = await Axios.post(
                `${NODE_HOST_URL_v1}/whatsappFlow`,
                { user_id: userId, method: 'deleteFlow', flow_id: deleteTarget.id },
                { headers: { Authorization: `Bearer ${userToken}` } }
            );
            if (data.success) {
                showAlert('Flow deleted successfully.');
                setDeleteTarget(null);
                fetchFlows();
            } else {
                showAlert(data.message || 'Failed to delete flow.', 'danger');
            }
        } catch (err) {
            showAlert(err.response?.data?.message || err.message, 'danger');
        } finally {
            setActionLoading(false);
        }
    };

    /* ── render ── */
    return (
        <>
            <DashboardLayout>
                <main className="content">

                    {/* Page Header */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px', marginBottom: '22px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <span style={{ width: '42px', height: '42px', borderRadius: '12px', background: '#dcfce7', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                                <FontAwesomeIcon icon={faCodeBranch} style={{ color: '#16a34a', fontSize: '18px' }} />
                            </span>
                            <div>
                                <h4 style={{ margin: 0, fontWeight: '700', color: '#1a1a2e', fontSize: '20px' }}>WhatsApp Flow Builder</h4>
                                <p style={{ margin: 0, color: '#64748b', fontSize: '13px' }}>Create and manage your WhatsApp flows.</p>
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                            <button
                                onClick={handleMigrateFlow}
                                disabled={migrateLoading}
                                style={{
                                    display: 'inline-flex', alignItems: 'center', gap: '7px',
                                    padding: '9px 18px', borderRadius: '10px',
                                    background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                                    color: '#fff', border: 'none', cursor: migrateLoading ? 'not-allowed' : 'pointer',
                                    fontSize: '13px', fontWeight: '600',
                                    boxShadow: '0 4px 14px rgba(99,102,241,0.35)',
                                    opacity: migrateLoading ? 0.75 : 1,
                                }}
                            >
                                {migrateLoading
                                    ? <><FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '12px' }} /> Migrating...</>
                                    : <><FontAwesomeIcon icon={faCodeBranch} style={{ fontSize: '12px' }} /> Migrate Flow</>
                                }
                            </button>
                            <button
                                onClick={openBuilder}
                                style={{
                                    display: 'inline-flex', alignItems: 'center', gap: '7px',
                                    padding: '9px 18px', borderRadius: '10px',
                                    background: 'linear-gradient(135deg, #25d366 0%, #128c7e 100%)',
                                    color: '#fff', border: 'none', cursor: 'pointer',
                                    fontSize: '13px', fontWeight: '600',
                                    boxShadow: '0 4px 14px rgba(37,211,102,0.35)',
                                }}
                            >
                                <FontAwesomeIcon icon={faPlus} style={{ fontSize: '12px' }} />
                                Create New Flow
                            </button>
                        </div>
                    </div>

                    {/* Alert */}
                    {alert.show && (
                        <div className={`alert alert-${alert.type}`} role="alert" style={{ marginBottom: '16px' }}>
                            <button type="button" className="close" onClick={() => setAlert({ show: false })}><span>&times;</span></button>
                            {alert.msg}
                        </div>
                    )}

                    {/* Flow List */}
                    <div style={{ background: '#fff', borderRadius: '14px', boxShadow: '0 1px 6px rgba(0,0,0,0.07)', border: '1px solid #e8e8f0', overflow: 'hidden', fontFamily: 'Inter, system-ui, sans-serif' }}>
                        <div style={{ padding: '16px 24px', borderBottom: '1px solid #f0f0f8', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <h6 style={{ margin: 0, fontWeight: '700', color: '#1a1a2e', fontSize: '15px' }}>All Flows</h6>
                            {flows.length > 0 && (
                                <span style={{ fontSize: '11px', fontWeight: '700', background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0', borderRadius: '20px', padding: '3px 10px' }}>
                                    {flows.length} {flows.length === 1 ? 'Flow' : 'Flows'}
                                </span>
                            )}
                        </div>
                        {loading ? (
                            <div style={{ textAlign: 'center', padding: '70px 0', color: '#64748b' }}>
                                <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '22px', display: 'block', margin: '0 auto 10px' }} />
                                Loading flows...
                            </div>
                        ) : flows.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '70px 24px', color: '#94a3b8' }}>
                                <FontAwesomeIcon icon={faCodeBranch} style={{ fontSize: '36px', display: 'block', margin: '0 auto 12px', opacity: 0.35 }} />
                                <p style={{ fontSize: '14px', fontWeight: '600', margin: '0 0 4px', color: '#64748b' }}>No flows yet</p>
                                <p style={{ fontSize: '13px', margin: 0 }}>Click "Add New Flow" to create your first flow.</p>
                            </div>
                        ) : (
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '700px' }}>
                                    <thead>
                                        <tr style={{ background: '#2d2d4e' }}>
                                            {['Flow Name', 'Flow ID' , 'Category', 'Status', 'Created', 'Actions'].map((h, i) => (
                                                <th key={i} style={{ padding: '13px 18px', fontSize: '11px', fontWeight: '700', color: '#e2e2f0', textAlign: 'left', whiteSpace: 'nowrap', letterSpacing: '0.4px', textTransform: 'uppercase' }}>{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {flows.map((flow, i) => (
                                            <tr key={flow.id || i} style={{ background: i % 2 === 0 ? '#f7f7fc' : '#fff', borderBottom: '1px solid #ebebf5' }}>
                                                {/* <td style={{ padding: '13px 18px', fontSize: '12px', color: '#94a3b8', fontWeight: '500', width: '48px' }}>{i + 1}</td> */}
                                                <td style={{ padding: '13px 18px' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                        <span style={{ width: '32px', height: '32px', borderRadius: '8px', background: '#dcfce7', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                            <FontAwesomeIcon icon={faCodeBranch} style={{ color: '#16a34a', fontSize: '13px' }} />
                                                        </span>
                                                        <span style={{ fontSize: '13px', color: '#1e293b', fontWeight: '600' }}>{flow.flow_title || '-'}</span>
                                                    </div>
                                                </td>
                                                <td style={{ padding: '13px 18px', fontSize: '12px', color: '#475569', whiteSpace: 'nowrap' }}>{flow.meta_flow_id}</td>
                                                <td style={{ padding: '13px 18px' }}>
                                                    <span style={{ fontSize: '11px', fontWeight: '600', padding: '3px 9px', borderRadius: '6px', background: '#f0f0f8', color: '#4f46e5' }}>
                                                        {flow.category ? flow.category.replace(/_/g, ' ') : '-'}
                                                    </span>
                                                </td>
                                                <td style={{ padding: '13px 18px' }}><StatusBadge status={resolveFlowStatus(flow)} /></td>
                                                <td style={{ padding: '13px 18px', fontSize: '12px', color: '#475569', whiteSpace: 'nowrap' }}>{flow.created || '-'}</td>
                                                <td style={{ padding: '13px 18px' }}>
                                                    <div style={{ display: 'flex', gap: '6px' }}>
                                                        <button title="Edit" onClick={() => openEditFlow(flow)} style={{ width: '32px', height: '32px', borderRadius: '8px', border: '1px solid #e2e8f0', background: '#fff', color: '#4f46e5', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                                                            <FontAwesomeIcon icon={faEye} style={{ fontSize: '12px' }} />
                                                        </button>
                                                        <button title="Button Tracker" onClick={() => history.push(`/dashboard/whatsapp-flow-builder-tracker/${flow.meta_flow_id}`)} style={{ width: '32px', height: '32px', borderRadius: '8px', border: '1px solid #bfdbfe', background: '#fff', color: '#2563eb', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                                                            <FontAwesomeIcon icon={faChartBar} style={{ fontSize: '12px' }} />
                                                        </button>
                                                        <button title="Delete" onClick={() => setDeleteTarget(flow)} style={{ width: '32px', height: '32px', borderRadius: '8px', border: '1px solid #fecaca', background: '#fff', color: '#dc2626', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                                                            <FontAwesomeIcon icon={faTrashAlt} style={{ fontSize: '12px' }} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    {/* ══════════════ FLOW BUILDER MODAL ══════════════ */}
                    {builderOpen && (
                        <div style={{ position: 'fixed', inset: 0, zIndex: 1050, display: 'flex', alignItems: isMobile ? 'flex-start' : 'center', justifyContent: 'center' }}>
                            <div style={{ position: 'absolute', inset: 0, background: 'rgba(10,20,40,0.6)', backdropFilter: 'blur(3px)' }} />
                            <div style={{
                                position: 'relative', background: '#fff', borderRadius: isMobile ? 0 : '14px',
                                boxShadow: '0 24px 80px rgba(0,0,0,0.3)',
                                width: isMobile ? '100vw' : '94vw', maxWidth: isMobile ? '100vw' : '1100px',
                                height: isMobile ? '100dvh' : '88vh', display: 'flex', flexDirection: 'column',
                                fontFamily: 'Inter, system-ui, sans-serif', overflow: 'hidden',
                            }}>

                                {/* ── Modal Header ── */}
                                <div style={{
                                    padding: '14px 20px', borderBottom: '1px solid #e8e8f0',
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    background: '#fff', flexShrink: 0,
                                }}>
                                    <h6 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#1a1a2e' }}>Create flow</h6>
                                    <button
                                        onClick={() => setBuilderOpen(false)}
                                        style={{ width: '30px', height: '30px', border: '1px solid #e2e8f0', borderRadius: '8px', background: '#f8fafc', color: '#64748b', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                                    >
                                        <FontAwesomeIcon icon={faTimes} style={{ fontSize: '13px' }} />
                                    </button>
                                </div>

                                {/* ── Mobile Tab Bar ── */}
                                {isMobile && (
                                    <div style={{ display: 'flex', borderBottom: '2px solid #e8e8f0', background: '#fff', flexShrink: 0 }}>
                                        {[{ key: 'screens', label: 'Screens' }, { key: 'edit', label: 'Edit' }, { key: 'preview', label: 'Preview' }].map(tab => (
                                            <button key={tab.key} onClick={() => setBuilderMobileTab(tab.key)} style={{
                                                flex: 1, padding: '10px 4px', border: 'none',
                                                borderBottom: builderMobileTab === tab.key ? '2px solid #4f46e5' : '2px solid transparent',
                                                marginBottom: '-2px', background: 'none', fontSize: '13px',
                                                fontWeight: builderMobileTab === tab.key ? '700' : '500',
                                                color: builderMobileTab === tab.key ? '#4f46e5' : '#64748b', cursor: 'pointer',
                                            }}>{tab.label}</button>
                                        ))}
                                    </div>
                                )}

                                {/* ── Modal Body ── */}
                                <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

                                    {/* LEFT: Screens panel */}
                                    <div style={{ width: isMobile ? '100%' : isTablet ? '160px' : '200px', flexShrink: 0, borderRight: isMobile ? 'none' : '1px solid #e8e8f0', background: '#fafafa', display: isMobile && builderMobileTab !== 'screens' ? 'none' : 'flex', flexDirection: 'column', overflowY: 'auto' }}>
                                        <div style={{ padding: '14px 16px 8px', fontSize: '12px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                            Screens
                                        </div>
                                        {screens.map((s) => (
                                            <div
                                                key={s.id}
                                                onClick={() => { setActiveId(s.id); if (isMobile) setBuilderMobileTab('edit'); }}
                                                style={{
                                                    display: 'flex', alignItems: 'center', gap: '8px',
                                                    padding: '10px 12px', cursor: 'pointer', margin: '2px 8px',
                                                    borderRadius: '8px',
                                                    background: s.id === (activeScreen?.id) ? '#e0e7ff' : 'transparent',
                                                    border: s.id === (activeScreen?.id) ? '1px solid #c7d2fe' : '1px solid transparent',
                                                }}
                                            >
                                                <FontAwesomeIcon icon={faGripVertical} style={{ color: '#cbd5e1', fontSize: '11px', flexShrink: 0 }} />
                                                <span style={{ fontSize: '13px', fontWeight: s.id === (activeScreen?.id) ? '600' : '400', color: s.id === (activeScreen?.id) ? '#4f46e5' : '#475569', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {s.title || 'Untitled'}
                                                </span>
                                                {screens.length > 1 && (
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); removeScreen(s.id); }}
                                                        style={{ border: 'none', background: 'none', color: '#dc2626', cursor: 'pointer', padding: '0 2px', fontSize: '10px' }}
                                                    >
                                                        <FontAwesomeIcon icon={faTimes} />
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                        {!showAddScreen ? (
                                            <button
                                                onClick={() => { setShowAddScreen(true); setNewScreenName(''); }}
                                                style={{ margin: '8px 12px', padding: '8px', border: 'none', background: 'none', color: '#4f46e5', cursor: 'pointer', fontSize: '13px', fontWeight: '600', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '6px' }}
                                            >
                                                <FontAwesomeIcon icon={faPlus} style={{ fontSize: '11px' }} />
                                                Add new
                                            </button>
                                        ) : (
                                            <div style={{ margin: '8px', padding: '10px 12px', background: '#f0f4ff', borderRadius: '10px', border: '1px solid #c7d2fe' }}>
                                                <input
                                                    type="text"
                                                    autoFocus
                                                    className="form-control form-control-sm"
                                                    placeholder="Screen name"
                                                    value={newScreenName}
                                                    onChange={e => setNewScreenName(e.target.value)}
                                                    onKeyDown={e => { if (e.key === 'Enter') addScreen(); if (e.key === 'Escape') { setShowAddScreen(false); setNewScreenName(''); } }}
                                                    style={{ fontSize: '12px', marginBottom: '8px' }}
                                                />
                                                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '6px' }}>
                                                    <button
                                                        onClick={() => { setShowAddScreen(false); setNewScreenName(''); }}
                                                        style={{ padding: '4px 10px', borderRadius: '6px', border: '1px solid #c7d2fe', background: '#fff', color: '#64748b', fontSize: '12px', cursor: 'pointer' }}
                                                    >
                                                        Cancel
                                                    </button>
                                                    <button
                                                        onClick={addScreen}
                                                        disabled={!newScreenName.trim()}
                                                        style={{ padding: '4px 14px', borderRadius: '6px', border: 'none', background: newScreenName.trim() ? '#4f46e5' : '#c7d2fe', color: '#fff', fontSize: '12px', fontWeight: '600', cursor: newScreenName.trim() ? 'pointer' : 'not-allowed' }}
                                                    >
                                                        Add
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* MIDDLE: Edit content */}
                                    <div style={{ flex: 1, overflowY: 'auto', background: '#fff', borderRight: isMobile ? 'none' : '1px solid #e8e8f0', display: isMobile && builderMobileTab !== 'edit' ? 'none' : 'block' }}>
                                        <div style={{ padding: '12px 16px', borderBottom: '1px solid #e8e8f0', background: '#f8fafc', fontSize: '12px', fontWeight: '600', color: '#64748b' }}>
                                            Edit content
                                        </div>

                                        {/* Flow Name & Category */}
                                        <div style={{ padding: '12px 16px', borderBottom: '1px solid #e8e8f0' }}>
                                            <div style={{ marginBottom: '10px' }}>
                                                <label style={{ fontSize: '11px', fontWeight: '600', color: '#64748b', marginBottom: '4px', display: 'block' }}>Flow Name</label>
                                                <input
                                                    type="text"
                                                    className="form-control form-control-sm"
                                                    placeholder="Enter flow name"
                                                    value={flowName}
                                                    onChange={e => { setFlowName(e.target.value); setFieldErrors(p => ({ ...p, flowName: '' })); }}
                                                    style={{ fontSize: '13px', borderColor: fieldErrors.flowName ? '#ef4444' : '' }}
                                                />
                                                {fieldErrors.flowName && <div style={{ fontSize: '11px', color: '#ef4444', marginTop: '4px' }}>{fieldErrors.flowName}</div>}
                                            </div>
                                            <div>
                                                <label style={{ fontSize: '11px', fontWeight: '600', color: '#64748b', marginBottom: '4px', display: 'block' }}>Category</label>
                                                <select
                                                    className="form-control form-control-sm"
                                                    value={flowCategory}
                                                    onChange={e => { setFlowCategory(e.target.value); setFieldErrors(p => ({ ...p, flowCategory: '' })); }}
                                                    style={{ fontSize: '13px', borderColor: fieldErrors.flowCategory ? '#ef4444' : '' }}
                                                >
                                                    <option value="">Select category</option>
                                                    {CATEGORIES.map(cat => (
                                                        <option key={cat} value={cat}>{cat.replace(/_/g, ' ')}</option>
                                                    ))}
                                                </select>
                                                {fieldErrors.flowCategory && <div style={{ fontSize: '11px', color: '#ef4444', marginTop: '4px' }}>{fieldErrors.flowCategory}</div>}
                                            </div>
                                        </div>

                                        {/* Screen Title */}
                                        <Accordion
                                            title="Screen title"
                                            open={openSections.title}
                                            onToggle={() => setOpenSections(p => ({ ...p, title: !p.title }))}
                                        >
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Screen title"
                                                value={activeScreen?.title || ''}
                                                onChange={e => { updateScreen(activeScreen.id, { title: e.target.value }); setFieldErrors(p => ({ ...p, screenTitle: '' })); }}
                                                style={{ fontSize: '13px', marginBottom: '4px', borderColor: fieldErrors.screenTitle ? '#ef4444' : '' }}
                                            />
                                            {fieldErrors.screenTitle && <div style={{ fontSize: '11px', color: '#ef4444', marginBottom: '8px' }}>{fieldErrors.screenTitle}</div>}
                                            <div style={{ marginTop: '10px' }}>
                                                <select
                                                    className="form-control"
                                                    value={selectedBrandNumber}
                                                    onChange={e => { setSelectedBrandNumber(e.target.value); setFieldErrors(p => ({ ...p, brandNumber: '' })); }}
                                                    style={{ fontSize: '13px', borderColor: fieldErrors.brandNumber ? '#ef4444' : '' }}
                                                >
                                                    <option value="">Select Brand Number</option>
                                                    {brandNumberList.map((item, i) => (
                                                        <option key={i} value={item.brand_number}>
                                                            {item.brand_number}
                                                        </option>
                                                    ))}
                                                </select>
                                                {fieldErrors.brandNumber && <div style={{ fontSize: '11px', color: '#ef4444', marginTop: '4px' }}>{fieldErrors.brandNumber}</div>}
                                            </div>
                                        </Accordion>

                                        {/* Body */}
                                        <Accordion
                                            title={<span>Body <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '400' }}>· {activeScreen?.body.length === 0 ? "Select 'Add content' to start building your form. ..." : `${activeScreen?.body.length} item${activeScreen?.body.length !== 1 ? 's' : ''}`}</span></span>}
                                            open={openSections.body}
                                            onToggle={() => setOpenSections(p => ({ ...p, body: !p.body }))}
                                        >
                                            {activeScreen?.body.map((item, index) => (
                                                <div
                                                    key={item.id}
                                                    draggable
                                                    onDragStart={() => setDragBodyIndex(index)}
                                                    onDragOver={e => { e.preventDefault(); setDragBodyOver(index); }}
                                                    onDragEnd={() => { setDragBodyIndex(null); setDragBodyOver(null); }}
                                                    onDrop={e => {
                                                        e.preventDefault();
                                                        if (dragBodyIndex !== null && dragBodyIndex !== index) {
                                                            reorderBody(dragBodyIndex, index);
                                                        }
                                                        setDragBodyIndex(null);
                                                        setDragBodyOver(null);
                                                    }}
                                                    style={{
                                                        opacity: dragBodyIndex === index ? 0.4 : 1,
                                                        borderTop: dragBodyOver === index && dragBodyIndex !== index
                                                            ? '2px solid #4f46e5' : '2px solid transparent',
                                                        transition: 'opacity 0.15s',
                                                        cursor: 'grab',
                                                    }}
                                                >
                                                    <ContentItem
                                                        item={item}
                                                        onChange={patch => updateContentItem(item.id, patch)}
                                                        onRemove={() => removeContentItem(item.id)}
                                                    />
                                                </div>
                                            ))}

                                            {/* Add content button */}
                                            <div ref={addContentRef} style={{ position: 'relative', display: 'inline-block', marginTop: '4px' }}>
                                                {activeScreen?.body.length >= 8 ? (
                                                    <span style={{ fontSize: '12px', color: '#94a3b8', fontWeight: '500' }}>
                                                        Max 8 items reached
                                                    </span>
                                                ) : (
                                                <button
                                                    onClick={() => { setShowAddContent(v => !v); setHoveredCategory(null); }}
                                                    style={{
                                                        display: 'inline-flex', alignItems: 'center', gap: '7px',
                                                        padding: '7px 14px', borderRadius: '8px',
                                                        border: '1px solid #e2e8f0', background: '#fff',
                                                        fontSize: '13px', fontWeight: '600', color: '#374151', cursor: 'pointer',
                                                    }}
                                                >
                                                    <FontAwesomeIcon icon={faPlus} style={{ fontSize: '11px' }} />
                                                    Add content
                                                    <FontAwesomeIcon icon={faCaretDown} style={{ fontSize: '11px', color: '#94a3b8' }} />
                                                </button>
                                                )}

                                                {showAddContent && (
                                                    <div style={{
                                                        position: 'absolute', top: '110%', left: 0, zIndex: 100,
                                                        display: 'flex', alignItems: 'flex-start',
                                                        flexDirection: isMobile ? 'column' : 'row',
                                                    }}>
                                                        {/* Level 1 — categories */}
                                                        <div style={{
                                                            background: '#fff', borderRadius: '10px',
                                                            boxShadow: '0 8px 24px rgba(0,0,0,0.14)',
                                                            border: '1px solid #e2e8f0', minWidth: '180px', padding: '4px 0',
                                                        }}>
                                                            {CONTENT_MENU.map(cat => (
                                                                <button
                                                                    key={cat.key}
                                                                    onMouseEnter={() => !isMobile && setHoveredCategory(cat.key)}
                                                                    onClick={() => isMobile && setHoveredCategory(hoveredCategory === cat.key ? null : cat.key)}
                                                                    style={{
                                                                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                                                        width: '100%', padding: '9px 14px', border: 'none',
                                                                        background: hoveredCategory === cat.key ? '#f0f4ff' : 'none',
                                                                        cursor: 'pointer', fontSize: '13px', color: '#1a1a2e',
                                                                        textAlign: 'left', gap: '10px',
                                                                    }}
                                                                >
                                                                    <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                                        <span style={{
                                                                            width: '24px', height: '24px', borderRadius: '6px',
                                                                            background: hoveredCategory === cat.key ? '#e0e7ff' : '#f1f5f9',
                                                                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                                                            fontSize: cat.iconText.length > 2 ? '12px' : '11px',
                                                                            fontWeight: '700', color: '#4f46e5', flexShrink: 0,
                                                                        }}>
                                                                            {cat.iconText}
                                                                        </span>
                                                                        {cat.label}
                                                                    </span>
                                                                    <span style={{ fontSize: '11px', color: '#94a3b8' }}>›</span>
                                                                </button>
                                                            ))}
                                                        </div>

                                                        {/* Level 2 — subtypes (shown when category hovered) */}
                                                        {hoveredCategory && (() => {
                                                            const cat = CONTENT_MENU.find(c => c.key === hoveredCategory);
                                                            if (!cat) return null;
                                                            return (
                                                                <div style={{
                                                                    background: '#fff', borderRadius: '10px',
                                                                    boxShadow: '0 8px 24px rgba(0,0,0,0.14)',
                                                                    border: '1px solid #e2e8f0', minWidth: '170px',
                                                                    padding: '4px 0', marginLeft: isMobile ? '0' : '4px', marginTop: isMobile ? '4px' : '0',
                                                                }}>
                                                                    {cat.children.map(child => (
                                                                        <button
                                                                            key={child.type}
                                                                            onClick={() => addContentItem(child.type)}
                                                                            style={{
                                                                                display: 'flex', alignItems: 'center', gap: '10px',
                                                                                width: '100%', padding: '9px 14px', border: 'none',
                                                                                background: 'none', cursor: 'pointer',
                                                                                fontSize: '13px', color: '#1a1a2e', textAlign: 'left',
                                                                            }}
                                                                            onMouseEnter={e => e.currentTarget.style.background = '#f0f4ff'}
                                                                            onMouseLeave={e => e.currentTarget.style.background = 'none'}
                                                                        >
                                                                            <span style={{
                                                                                fontSize: '11px', fontWeight: '700', color: '#4f46e5',
                                                                                width: '16px', textAlign: 'center', flexShrink: 0,
                                                                            }}>
                                                                                {child.iconText}
                                                                            </span>
                                                                            {child.label}
                                                                        </button>
                                                                    ))}
                                                                </div>
                                                            );
                                                        })()}
                                                    </div>
                                                )}
                                            </div>
                                        </Accordion>

                                        {/* Button */}
                                        <Accordion
                                            title={<span>Button <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '400' }}>· {activeScreen?.button || 'Continue'}</span></span>}
                                            open={openSections.button}
                                            onToggle={() => setOpenSections(p => ({ ...p, button: !p.button }))}
                                        >
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Button label"
                                                value={activeScreen?.button || ''}
                                                onChange={e => updateScreen(activeScreen.id, { button: e.target.value })}
                                                style={{ fontSize: '13px' }}
                                            />
                                        </Accordion>
                                    </div>

                                    {/* RIGHT: Preview */}
                                    <div style={{ width: isMobile ? '100%' : isTablet ? '260px' : '320px', flexShrink: 0, background: '#f1f5f9', display: isMobile && builderMobileTab !== 'preview' ? 'none' : 'flex', flexDirection: 'column', alignItems: 'center', padding: '20px 16px', overflowY: 'auto', position: 'relative' }}>
                                        <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                                            <span style={{ fontSize: '13px', fontWeight: '700', color: '#374151' }}>Preview</span>
                                            <div ref={settingsRef} style={{ position: 'relative' }}>
                                                <button
                                                    onClick={() => setShowSettings(v => !v)}
                                                    style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', background: '#fff', fontSize: '12px', fontWeight: '600', color: '#374151', cursor: 'pointer' }}
                                                >
                                                    <FontAwesomeIcon icon={faCog} style={{ fontSize: '12px' }} />
                                                    <FontAwesomeIcon icon={faCaretDown} style={{ fontSize: '10px', color: '#94a3b8' }} />
                                                </button>

                                                {showSettings && (
                                                    <div style={{ position: 'absolute', top: '110%', right: 0, zIndex: 200, background: '#fff', borderRadius: '12px', boxShadow: '0 10px 30px rgba(0,0,0,0.18)', border: '1px solid #e2e8f0', minWidth: '230px', padding: '6px 0', overflow: 'hidden' }}>

                                                        {/* Interactive mode */}
                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', borderBottom: '1px solid #f0f0f8' }}>
                                                            <span style={{ fontSize: '13px', fontWeight: '600', color: '#1a1a2e' }}>Interactive mode</span>
                                                            {/* Toggle switch */}
                                                            <div
                                                                onClick={() => setPreviewSettings(p => ({ ...p, interactive: !p.interactive }))}
                                                                style={{
                                                                    width: '40px', height: '22px', borderRadius: '11px', cursor: 'pointer',
                                                                    background: previewSettings.interactive ? '#1a1a2e' : '#e2e8f0',
                                                                    position: 'relative', transition: 'background 0.2s', flexShrink: 0,
                                                                }}
                                                            >
                                                                <div style={{
                                                                    position: 'absolute', top: '3px',
                                                                    left: previewSettings.interactive ? '21px' : '3px',
                                                                    width: '16px', height: '16px', borderRadius: '50%',
                                                                    background: '#fff', transition: 'left 0.2s',
                                                                    boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
                                                                }} />
                                                            </div>
                                                        </div>

                                                        {/* Platform */}
                                                        <div style={{ padding: '10px 16px 4px' }}>
                                                            <span style={{ fontSize: '11px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Platform</span>
                                                        </div>
                                                        {[
                                                            { value: 'android', label: 'Android', emoji: '🤖' },
                                                            { value: 'ios', label: 'iOS', emoji: '🍎' },
                                                        ].map(opt => (
                                                            <button
                                                                key={opt.value}
                                                                onClick={() => setPreviewSettings(p => ({ ...p, platform: opt.value }))}
                                                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', padding: '9px 16px', border: 'none', background: 'none', cursor: 'pointer', fontSize: '13px', color: '#1a1a2e' }}
                                                                onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                                                                onMouseLeave={e => e.currentTarget.style.background = 'none'}
                                                            >
                                                                <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                                    <span style={{ fontSize: '15px' }}>{opt.emoji}</span>
                                                                    {opt.label}
                                                                </span>
                                                                {previewSettings.platform === opt.value && (
                                                                    <FontAwesomeIcon icon={faCheck} style={{ fontSize: '12px', color: '#4f46e5' }} />
                                                                )}
                                                            </button>
                                                        ))}

                                                        {/* Theme */}
                                                        <div style={{ padding: '10px 16px 4px', borderTop: '1px solid #f0f0f8', marginTop: '4px' }}>
                                                            <span style={{ fontSize: '11px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Theme</span>
                                                        </div>
                                                        {[
                                                            { value: 'light', label: 'Light', icon: faSun },
                                                            { value: 'dark', label: 'Dark', icon: faMoon },
                                                        ].map(opt => (
                                                            <button
                                                                key={opt.value}
                                                                onClick={() => setPreviewSettings(p => ({ ...p, theme: opt.value }))}
                                                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', padding: '9px 16px', border: 'none', background: 'none', cursor: 'pointer', fontSize: '13px', color: '#1a1a2e' }}
                                                                onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                                                                onMouseLeave={e => e.currentTarget.style.background = 'none'}
                                                            >
                                                                <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                                    <FontAwesomeIcon icon={opt.icon} style={{ fontSize: '13px', color: '#64748b', width: '15px' }} />
                                                                    {opt.label}
                                                                </span>
                                                                {previewSettings.theme === opt.value && (
                                                                    <FontAwesomeIcon icon={faCheck} style={{ fontSize: '12px', color: '#4f46e5' }} />
                                                                )}
                                                            </button>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <PhonePreview
                                            screen={activeScreen || screens[0]}
                                            platform={previewSettings.platform}
                                            theme={previewSettings.theme}
                                            interactive={previewSettings.interactive}
                                        />
                                        <p style={{ marginTop: '16px', fontSize: '11px', color: '#94a3b8', textAlign: 'center', lineHeight: '1.5' }}>
                                            Rendering and interaction varies based on device.
                                        </p>
                                    </div>
                                </div>

                                {/* ── In-modal alert ── */}
                                {alert.show && (
                                    <div style={{
                                        padding: '10px 16px',
                                        borderTop: '1px solid #e8e8f0',
                                        background: alert.type === 'danger' ? '#fef2f2' : '#f0fdf4',
                                        borderBottom: `2px solid ${alert.type === 'danger' ? '#fca5a5' : '#86efac'}`,
                                        color: alert.type === 'danger' ? '#dc2626' : '#16a34a',
                                        fontSize: '13px',
                                        fontWeight: '500',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: '10px',
                                        flexShrink: 0,
                                    }}>
                                        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <span style={{ fontSize: '15px' }}>{alert.type === 'danger' ? '⚠' : '✓'}</span>
                                            {alert.msg}
                                        </span>
                                        <button
                                            onClick={() => setAlert({ show: false })}
                                            style={{ border: 'none', background: 'none', color: 'inherit', cursor: 'pointer', fontSize: '18px', lineHeight: 1, padding: '0 4px', opacity: 0.6, flexShrink: 0 }}
                                        >
                                            ×
                                        </button>
                                    </div>
                                )}

                                {/* ── Modal Footer ── */}
                                <div style={{
                                    padding: '12px 20px', borderTop: '1px solid #e8e8f0',
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    background: '#fafafa', flexShrink: 0,
                                }}>
                                    <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                                        Once created, this flow cannot be edited.
                                    </span>
                                    <div style={{ display: 'flex', gap: '10px' }}>
                                        <button
                                            onClick={() => setBuilderOpen(false)}
                                            style={{ padding: '8px 18px', borderRadius: '8px', border: '1px solid #e2e8f0', background: '#fff', color: '#374151', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={handleSave}
                                            disabled={actionLoading}
                                            style={{
                                                padding: '8px 22px', borderRadius: '8px',
                                                background: '#2563eb', color: '#fff', border: 'none',
                                                fontSize: '13px', fontWeight: '600', cursor: actionLoading ? 'not-allowed' : 'pointer',
                                                display: 'inline-flex', alignItems: 'center', gap: '7px',
                                                opacity: actionLoading ? 0.75 : 1,
                                            }}
                                        >
                                            {actionLoading ? <><FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '12px' }} /> Saving...</> : 'Save'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Delete Confirm Modal */}
                    {!!deleteTarget && (
                        <div style={{ position: 'fixed', inset: 0, zIndex: 1060, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <div onClick={() => setDeleteTarget(null)} style={{ position: 'absolute', inset: 0, background: 'rgba(15,23,42,0.45)' }} />
                            <div style={{ position: 'relative', background: '#fff', borderRadius: '16px', boxShadow: '0 20px 60px rgba(0,0,0,0.18)', width: '100%', maxWidth: '420px', margin: '0 16px', padding: '28px 24px', textAlign: 'center' }}>
                                <span style={{ width: '52px', height: '52px', borderRadius: '50%', background: '#fef2f2', border: '2px solid #fecaca', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px' }}>
                                    <FontAwesomeIcon icon={faTrashAlt} style={{ color: '#dc2626', fontSize: '20px' }} />
                                </span>
                                <h6 style={{ fontWeight: '700', color: '#1e293b', fontSize: '16px', margin: '0 0 8px' }}>Delete Flow</h6>
                                <p style={{ color: '#64748b', fontSize: '13px', margin: '0 0 24px' }}>
                                    Are you sure you want to delete <strong style={{ color: '#1e293b' }}>{deleteTarget?.flow_title}</strong>?<br />This action cannot be undone.
                                </p>
                                <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                                    <button onClick={() => setDeleteTarget(null)} style={{ padding: '9px 20px', borderRadius: '9px', border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>Cancel</button>
                                    <button onClick={handleDelete} disabled={actionLoading} style={{ padding: '9px 20px', borderRadius: '9px', background: '#dc2626', color: '#fff', border: 'none', fontSize: '13px', fontWeight: '600', cursor: actionLoading ? 'not-allowed' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: '7px', opacity: actionLoading ? 0.75 : 1 }}>
                                        {actionLoading ? <><FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '12px' }} /> Deleting...</> : 'Yes, Delete'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Button Tracker Modal */}
                    {!!trackerTarget && (
                        <div style={{ position: 'fixed', inset: 0, zIndex: 1060, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <div onClick={() => setTrackerTarget(null)} style={{ position: 'absolute', inset: 0, background: 'rgba(15,23,42,0.45)' }} />
                            <div style={{ position: 'relative', background: '#fff', borderRadius: '16px', boxShadow: '0 20px 60px rgba(0,0,0,0.18)', width: '100%', maxWidth: '560px', margin: '0 16px', fontFamily: 'Inter, system-ui, sans-serif', overflow: 'hidden' }}>
                                {/* Header */}
                                <div style={{ padding: '18px 22px', borderBottom: '1px solid #e8e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <span style={{ width: '36px', height: '36px', borderRadius: '10px', background: '#eff6ff', border: '1px solid #bfdbfe', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <FontAwesomeIcon icon={faChartBar} style={{ color: '#2563eb', fontSize: '15px' }} />
                                        </span>
                                        <div>
                                            <div style={{ fontWeight: '700', fontSize: '14px', color: '#1e293b' }}>Button Tracker</div>
                                            <div style={{ fontSize: '12px', color: '#64748b', marginTop: '1px' }}>{trackerTarget.flow_title}</div>
                                        </div>
                                    </div>
                                    <button onClick={() => setTrackerTarget(null)} style={{ width: '30px', height: '30px', border: '1px solid #e2e8f0', borderRadius: '8px', background: '#f8fafc', color: '#64748b', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <FontAwesomeIcon icon={faTimes} style={{ fontSize: '12px' }} />
                                    </button>
                                </div>

                                {/* Body */}
                                <div style={{ padding: '20px 22px', maxHeight: '420px', overflowY: 'auto' }}>
                                    {trackerLoading ? (
                                        <div style={{ textAlign: 'center', padding: '40px 0', color: '#64748b' }}>
                                            <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '22px', display: 'block', margin: '0 auto 10px' }} />
                                            Loading tracker data…
                                        </div>
                                    ) : trackerData.length === 0 ? (
                                        <div style={{ textAlign: 'center', padding: '40px 0' }}>
                                            <span style={{ fontSize: '36px', display: 'block', marginBottom: '12px', opacity: 0.35 }}>📊</span>
                                            <p style={{ fontWeight: '600', fontSize: '14px', color: '#64748b', margin: '0 0 4px' }}>No tracker data yet</p>
                                            <p style={{ fontSize: '12px', color: '#94a3b8', margin: 0 }}>Button interactions will appear here once users start engaging with this flow.</p>
                                        </div>
                                    ) : (
                                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                            <thead>
                                                <tr style={{ background: '#f8fafc' }}>
                                                    {['Screen', 'Button Label', 'Clicks', 'Rate'].map((h, i) => (
                                                        <th key={i} style={{ padding: '9px 12px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px', borderBottom: '1px solid #e2e8f0' }}>{h}</th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {trackerData.map((row, i) => {
                                                    const rate = row.impressions > 0 ? Math.round((row.clicks / row.impressions) * 100) : 0;
                                                    return (
                                                        <tr key={i} style={{ borderBottom: '1px solid #f0f0f8' }}>
                                                            <td style={{ padding: '10px 12px', color: '#475569', fontWeight: '500' }}>{row.screen || '-'}</td>
                                                            <td style={{ padding: '10px 12px' }}>
                                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#f0f4ff', color: '#4f46e5', borderRadius: '6px', padding: '2px 8px', fontSize: '12px', fontWeight: '600' }}>
                                                                    {row.button || '-'}
                                                                </span>
                                                            </td>
                                                            <td style={{ padding: '10px 12px', color: '#1e293b', fontWeight: '700' }}>{(row.clicks || 0).toLocaleString()}</td>
                                                            <td style={{ padding: '10px 12px', minWidth: '120px' }}>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                    <div style={{ flex: 1, height: '6px', borderRadius: '3px', background: '#e2e8f0', overflow: 'hidden' }}>
                                                                        <div style={{ width: `${rate}%`, height: '100%', borderRadius: '3px', background: rate >= 60 ? '#16a34a' : rate >= 30 ? '#d97706' : '#2563eb', transition: 'width 0.4s' }} />
                                                                    </div>
                                                                    <span style={{ fontSize: '11px', fontWeight: '700', color: '#475569', minWidth: '30px', textAlign: 'right' }}>{rate}%</span>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    )}
                                </div>

                                {/* Footer */}
                                <div style={{ padding: '12px 22px', borderTop: '1px solid #e8e8f0', display: 'flex', justifyContent: 'flex-end', background: '#fafafa' }}>
                                    <button onClick={() => setTrackerTarget(null)} style={{ padding: '8px 20px', borderRadius: '8px', border: '1px solid #e2e8f0', background: '#fff', color: '#374151', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                </main>
            </DashboardLayout>

            {/* ══ EDIT FLOW VIEW — portal so position:fixed is never clipped ══ */}
            {editTarget && ReactDOM.createPortal(
                <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', flexDirection: 'column', background: '#fff', fontFamily: 'Inter, system-ui, sans-serif' }}>

                    {/* Header */}
                    <div style={{ borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', flexWrap: 'wrap', padding: isMobile ? '8px 20px 0' : '0 20px', gap: '12px', flexShrink: 0, minHeight: '52px' }}>
                        <button onClick={() => setEditTarget(null)} title="Close" style={{ width: '30px', height: '30px', border: '1px solid #e2e8f0', borderRadius: '8px', background: '#f8fafc', color: '#64748b', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <FontAwesomeIcon icon={faTimes} style={{ fontSize: '12px' }} />
                        </button>
                        <span style={{ fontWeight: '700', fontSize: '14px', color: '#1a1a2e', maxWidth: isMobile ? '180px' : '340px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{editTarget.flow_title}</span>
                        <StatusBadge status={resolveFlowStatus(editTarget)} />
                        {!isMobile && <span style={{ fontSize: '12px', color: '#94a3b8' }}>• Updated {editTarget.created || '-'}</span>}
                        {isMobile && (
                            <div style={{ width: 'calc(100% + 40px)', marginLeft: '-20px', display: 'flex', borderTop: '1px solid #e2e8f0', marginTop: '8px' }}>
                                {[{ key: 'editor', label: 'Editor' }, { key: 'preview', label: 'Preview' }].map(tab => (
                                    <button key={tab.key} onClick={() => setEditMobileTab(tab.key)} style={{
                                        flex: 1, padding: '10px 4px', border: 'none',
                                        borderBottom: editMobileTab === tab.key ? '2px solid #4f46e5' : '2px solid transparent',
                                        background: 'none', fontSize: '13px',
                                        fontWeight: editMobileTab === tab.key ? '700' : '500',
                                        color: editMobileTab === tab.key ? '#4f46e5' : '#64748b', cursor: 'pointer',
                                    }}>{tab.label}</button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Body */}
                    <div style={{ flex: 1, display: 'flex', overflow: 'hidden', position: 'relative' }}>

                        {/* Loading overlay while fetching flow_json */}
                        {editLoading && (
                            <div style={{ position: 'absolute', inset: 0, zIndex: 10, background: 'rgba(255,255,255,0.75)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
                                <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '28px', color: '#4f46e5' }} />
                                <span style={{ fontSize: '13px', color: '#475569', fontWeight: '500' }}>Loading flow JSON…</span>
                            </div>
                        )}

                        {/* LEFT — Editor */}
                        <div style={{ flex: 1, display: isMobile && editMobileTab !== 'editor' ? 'none' : 'flex', flexDirection: 'column', borderRight: isMobile ? 'none' : '1px solid #e2e8f0', overflow: 'hidden', minWidth: 0 }}>
                            <div style={{ padding: '10px 16px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fff', flexShrink: 0 }}>
                                <span style={{ fontSize: '13px', fontWeight: '600', color: '#1a1a2e' }}>Editor</span>
                                <button onClick={handleRunJson} style={{ padding: '5px 18px', borderRadius: '6px', border: '1px solid #d1d5db', background: '#fff', fontSize: '12px', fontWeight: '600', color: '#1a1a2e', cursor: 'pointer' }}>Run</button>
                            </div>
                            <textarea
                                value={editJson}
                                onChange={e => setEditJson(e.target.value)}
                                onKeyDown={e => {
                                    if (e.key === 'Tab') {
                                        e.preventDefault();
                                        const s = e.target.selectionStart, en = e.target.selectionEnd;
                                        const v = editJson.substring(0, s) + '  ' + editJson.substring(en);
                                        setEditJson(v);
                                        setTimeout(() => { e.target.selectionStart = e.target.selectionEnd = s + 2; }, 0);
                                    }
                                }}
                                spellCheck={false}
                                style={{ flex: 1, fontFamily: '"Fira Code", "Cascadia Code", Consolas, monospace', fontSize: '13px', lineHeight: '1.75', padding: '16px 20px', border: 'none', outline: 'none', resize: 'none', color: '#1e293b', background: '#fff' }}
                            />
                        </div>

                        {/* RIGHT — Preview */}
                        <div style={{ width: isMobile ? '100%' : '420px', flexShrink: 0, background: '#f1f5f9', display: isMobile && editMobileTab !== 'preview' ? 'none' : 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                            <div style={{ padding: '10px 16px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fff', flexShrink: 0 }}>
                                <span style={{ fontSize: '13px', fontWeight: '600', color: '#1a1a2e' }}>Preview</span>
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    {getEditPreviewScreens().length > 0 && (
                                        <select value={editPreviewScreenIdx} onChange={e => setEditPreviewScreenIdx(+e.target.value)} style={{ fontSize: '12px', padding: '5px 10px', border: '1px solid #e2e8f0', borderRadius: '6px', background: '#fff', color: '#1a1a2e', cursor: 'pointer', outline: 'none' }}>
                                            {getEditPreviewScreens().map((s, i) => <option key={i} value={i}>{s.id || `Screen ${i + 1}`}</option>)}
                                        </select>
                                    )}
                                    <button style={{ width: '30px', height: '30px', border: '1px solid #e2e8f0', borderRadius: '6px', background: '#fff', color: '#64748b', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <FontAwesomeIcon icon={faCog} style={{ fontSize: '12px' }} />
                                    </button>
                                </div>
                            </div>
                            <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '30px 20px' }}>
                                {editJsonError ? (
                                    <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', padding: '20px', color: '#dc2626', fontSize: '12px', lineHeight: '1.6', width: '100%', maxWidth: '340px' }}>
                                        <strong>JSON Error</strong><br />{editJsonError}
                                    </div>
                                ) : (
                                    <>
                                        <PhonePreview screen={waScreenToPreviewScreen(getEditPreviewScreens()[editPreviewScreenIdx])} platform="android" theme="light" interactive={true} />
                                        <p style={{ marginTop: '16px', fontSize: '11px', color: '#94a3b8', textAlign: 'center', lineHeight: '1.5' }}>Rendering and interaction varies based on device. ℹ</p>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Footer bar */}
                    <div style={{ height: '42px', borderTop: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', padding: '0 16px', background: '#fafafa', flexShrink: 0, overflowX: 'auto' }}>
                        <span style={{ fontSize: '12px', color: '#1a1a2e', display: 'flex', alignItems: 'center', gap: '7px' }}>
                            Flow JSON errors
                            <span style={{ fontWeight: '700', fontSize: '11px', color: editJsonError ? '#dc2626' : '#16a34a', background: editJsonError ? '#fef2f2' : '#f0fdf4', border: `1px solid ${editJsonError ? '#fecaca' : '#bbf7d0'}`, borderRadius: '20px', padding: '1px 9px' }}>
                                {editJsonError ? 1 : 0}
                            </span>
                        </span>
                        <span style={{ width: '1px', height: '20px', background: '#e2e8f0', margin: '0 18px' }} />
                        <span style={{ fontSize: '12px', color: '#64748b', cursor: 'pointer', fontWeight: '500' }}>Actions</span>
                        <span style={{ width: '1px', height: '20px', background: '#e2e8f0', margin: '0 18px' }} />
                        <span style={{ fontSize: '12px', color: '#64748b', cursor: 'pointer', fontWeight: '500' }}>Endpoint</span>
                        <div style={{ marginLeft: 'auto' }}>
                            <FontAwesomeIcon icon={faChevronUp} style={{ fontSize: '11px', color: '#94a3b8' }} />
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
};

export default WhatsappFlowBuilder;
